/* GTM programmes — the seven questions, and what is still open under each of them.

   Jihwan Kang via Max, 2026-10-05: "Please refer to Page 5 onward, starting with the '7 Questions.'"

   WHY THIS IS NOT A COPY OF THE DOCUMENT. The masterplan is a finished deck and it is good at being
   one. What it cannot do is hold its own live state, and from page 5 on the same three things repeat
   under every question: a decision nobody has taken, a fact nobody has verified, and a region that
   has not replied. Those are rows here; the prose stays prose.

   THE SUMMARY COMES FIRST. The stop conditions and the four counts sit above the seven questions,
   because somebody opening this wants to know what is blocked before they want to read. The
   questions themselves come from the SERVER with the programme — the same words the sections are
   stored under, so the screen cannot end up calling question 3 something the data does not. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
import { ArrowLeft, Plus, Check, X, FileDown, Trash2, Pencil, ChevronDown, ChevronUp, GripVertical,
  Eye, Mail, RotateCcw, CheckCircle2, Loader2 } from 'lucide-react';
import Header from './Header';
import ModuleTabs from './ModuleTabs';
import ModuleHelp from './ModuleHelp';
import RichText, { richToHtml } from './RichText';
import { DecisionDialog } from './DecisionDialog';
import { BACKEND_URL } from './backendConfig';
import './SPHub.css';
import './ProjectApproval.css';
import GtmRankList from './GtmRankList';
import GtmPickInput from './GtmPickInput';
import { GtmSpecTable, GtmDiscover, GtmTimeline, GtmMeasure } from './GtmTables';
import GtmBin from './GtmBin';
import GtmDeleteDialog from './GtmDeleteDialog';
import ProjectPhotos from './ProjectPhotos';
import GtmItemRow from './GtmItemRow';
import GtmSkuLinks from './GtmSkuLinks';
/* Project Approval's column tools, from where Project Approval imports them. */
import { SortTh } from './15_SampleTracker';
import useColumnTools from './useColumnTools';
import './Gtm.css';

const API = `${BACKEND_URL}/api/gtm`;

const get = async (url) => {
  const r = await fetch(url, { credentials: 'include' });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
  return j;
};
const send = async (url, method, body) => {
  const r = await fetch(url, {
    method, credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
  return j;
};

const day = (v) => (v ? String(v).slice(0, 10) : '');

/* ---- the two lists ------------------------------------------------------------------------- */
/* TWO SCREENS FOR TWO ROLES — Max, 2026-10-08: "hiện giao diện của người tạo và người approve ko
   tách ra nên nhiều phiếu GTM thì sẽ lẫn lộn vào nhau không tốt nhé ... cũng có 2 role cho GTM thì
   phải thiết kế cho hợp lý nhé".
   One list served both, so an approver read past everybody's drafts to find the one waiting on
   him and an author saw an Approve button that was never theirs. It is now the split the portal
   already has twice — Project Tracker / Project Approval, the pricing calculator / Pricing
   Approval:
     · GTM › Programmes (`gtm_programme`) is the AUTHOR's workspace: my programmes first, write,
       send for review, delete. No Approve anywhere on it.
     · GTM › Approval (`gtm_approval`) is the APPROVER's queue: only what has been sent, only what
       this person may decide, Project Approval's tabs. No Edit, no Delete, no drafts.
   Both use Project Approval's own classes (`pa-title`, `pa-tabs`, `pa-section`, `pa-table`,
   `pa-btn`), imported rather than copied, so they cannot drift from it or from each other. */
const REVIEW_STATES = ['draft', 'in_review', 'approved', 'rejected'];
const STATE_TONE = { draft: 'drop', in_review: 'pend', approved: 'conf', rejected: 'rew' };
const stateOf = (r) => (REVIEW_STATES.includes(r.status) ? r.status : 'draft');

/* THE SAME FETCH FROM EVERY BUTTON, and the filename comes from the SERVER's header: a file named
   one way from a list and another from the sheet would be two answers to what it is called. */
const downloadPdf = async (code) => {
  const r = await fetch(`${API}/${encodeURIComponent(code)}/pdf`, { credentials: 'include' });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || i18n.t('xGtm.pdfFailed'));
  const blob = await r.blob();
  const cd = r.headers.get('content-disposition') || '';
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = m ? decodeURIComponent(m[1]) : `${code}.pdf`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
};

const matches = (r, q) => !q || [r.gtm_code, r.title, r.headline, r.owner_name, r.approver_name, r.submitted_by]
  .some((v) => String(v || '').toLowerCase().includes(q));

/* GTM keeps its stamps in the server's LOCAL time (SYSDATETIME, +07) while the rest of the portal
   stores UTC — so `fmtDateTimeVN` here would add seven hours to every one. The stored wall clock is
   printed as it is. */
const stamp = (v) => (v ? String(v).slice(0, 16).replace('T', ' ') : '');
const dash = <span className="pa-muted">—</span>;

/* A sent programme stays put — the server refuses the delete too (DELETE_LOCKED in gtm.js). */
const DELETE_LOCKED = new Set(['in_review', 'approved']);
/* The two states in which the last decision is the CURRENT one. */
const DECIDED = new Set(['approved', 'rejected']);

/* ONE TABLE, DRAWN FROM ITS COLUMN LIST, so a header and the cells under it cannot come apart —
   which is what happens the first time somebody adds a <th> and forgets the <td>. A column with a
   `get` is sortable and filterable; one without is a plain heading. */
const GtmTable = ({ columns, tools, extraClass = '' }) => (
  <div className="pa-table-wrap">
    <table className={`pa-table gtm-ltable ${extraClass}`}>
      <thead>
        <tr>
          {columns.map((c) => (c.get
            ? <SortTh key={c.key} label={c.label} hint={c.hint} className={c.th || ''} {...tools.thProps(c.key)} />
            : <th key={c.key} className={c.th || ''}>{c.label}</th>))}
        </tr>
      </thead>
      <tbody>
        {tools.shown.map((r, i) => (
          <tr key={r.gtm_pk_id}>
            {columns.map((c) => (
              <td key={c.key} className={typeof c.td === 'function' ? c.td(r) : (c.td || '')}>
                {c.cell ? c.cell(r, i) : (c.get(r) === '' || c.get(r) === null || c.get(r) === undefined ? dash : c.get(r))}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* The columns both lists share, written once. */
const sharedColumns = (t, onOpen) => ({
  no: { key: 'no', label: t('gtm.colNo'), th: 'pa-no-col', td: 'pa-no-col', cell: (r, i) => i + 1 },
  code: { key: 'code', label: t('gtm.colCode'), get: (r) => r.gtm_code, td: 'mono' },
  title: {
    key: 'title', label: t('gtm.colTitle'), get: (r) => r.title, td: 'gtm-ltitle',
    cell: (r) => (
      <>
        <button type="button" className="pa-link pa-name-link" onClick={() => onOpen(r.gtm_code)}>{r.title}</button>
        {r.headline && <span className="gtm-lhead-line">{r.headline}</span>}
      </>
    ),
  },
  decisions: { key: 'decisions', label: t('gtm.colDecisions'), hint: t('gtm.openDecisions'), get: (r) => r.open_decisions,
    td: (r) => `num${r.open_decisions ? ' pa-warn' : ' pa-muted'}`, cell: (r) => r.open_decisions },
  verify: { key: 'verify', label: t('gtm.colVerify'), hint: t('gtm.openVerifies'), get: (r) => r.open_verifies,
    td: (r) => `num${r.open_verifies ? ' pa-warn' : ' pa-muted'}`, cell: (r) => r.open_verifies },
  /* How many Project SKUs it launched — the "connected to the Project Tracker" half of Jihwan's
     request, visible from the list without opening the sheet. */
  skus: { key: 'skus', label: t('gtm.colSkus'), hint: t('gtm.skuHead'), get: (r) => r.skus_linked || 0,
    td: (r) => `num${r.skus_linked ? '' : ' pa-muted'}`, cell: (r) => r.skus_linked || 0 },
  /* How many regions, no longer "waiting / all": the reply status went off the sheet with the
     other region columns (So-yeon, 2026-10-16), so "waiting" would count every region for ever. */
  regions: { key: 'regions', label: t('gtm.colRegions'), hint: t('gtm.regionsCount'), get: (r) => r.regions_all,
    td: (r) => `num${r.regions_all ? '' : ' pa-muted'}`, cell: (r) => r.regions_all },
  owner: { key: 'owner', label: t('gtm.colOwner'), get: (r) => r.owner_name || '' },
  approver: { key: 'approver', label: t('gtm.colApprover'), get: (r) => r.approver_name || '' },
  launch: { key: 'gtmLaunch', label: t('gtm.colLaunch'), get: (r) => day(r.launch_target), td: 'pa-when' },
});

/* THE MANAGER'S NOTE, one line until it is clicked — Project Approval's long-remark cell (Jay,
   2026-09-16), because a reason that wraps to five lines takes the whole row with it. */
const NoteCell = ({ text, open, onToggle }) => (text
  ? (
    <button type="button" className={`pa-note${open ? ' on' : ''}`} title={text}
      aria-expanded={open} onClick={onToggle}>{text}</button>
  ) : dash);

/* THE GUIDES (Max 01/11: every module has one, ending on the manual): one per screen, because /gtm and /gtm-approval show
   different lists. New programme is drawn only for someone who may author, Approve only on a row the reader may decide —
   TourGuide skips a step whose control is not there. */
const TOUR_PROGRAMMES = [
  { selector: '.gtm-scope', title: 'tourGtm.scopeT', body: 'tourGtm.scopeB' },
  { selector: '.gtm-kpis', title: 'tourGtm.kpisT', body: 'tourGtm.kpisB' },
  { selector: '.gtm-listpage .pa-toolbar .gtm-search', title: 'tourGtm.searchT', body: 'tourGtm.searchB' },
  { selector: '.gtm-listpage .pa-toolbar .pa-btn.primary', title: 'tourGtm.newT', body: 'tourGtm.newB' },
  { selector: '.gtm-ltable', title: 'tourGtm.listT', body: 'tourGtm.listB' },
];
const TOUR_APPROVAL = [
  { selector: '.gtm-listpage .pa-tabs', title: 'tourGtm.aTabsT', body: 'tourGtm.aTabsB' },
  { selector: '.gtm-listpage .pa-toolbar .gtm-search', title: 'tourGtm.searchT', body: 'tourGtm.searchB' },
  { selector: '.gtm-atable', title: 'tourGtm.aListT', body: 'tourGtm.aListB' },
  { selector: '.gtm-atable .pa-btn.approve', title: 'tourGtm.aDecideT', body: 'tourGtm.aDecideB' },
];

/* ---- GTM › Programmes: the author's workspace ------------------------------------------------- */
function ProgrammeList({ t, onOpen }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [delRow, setDelRow] = useState(null);
  const [pdfFor, setPdfFor] = useState(null);
  const [state, setState] = useState('');
  const [search, setSearch] = useState('');
  /* MINE FIRST. With forty programmes on the screen an author should not have to read everybody
     else's to find their own — which is the "lẫn lộn vào nhau" half of Max's point. All is one
     press away, because a programme is shared work and reading a colleague's is legitimate. */
  const [scope, setScope] = useState('mine');

  const load = useCallback(async () => {
    try {
      const j = await get(API);
      setRows(j.rows || []);
      setCanEdit(!!j.canEdit);
    } catch (e) { setMsg({ bad: true, text: e.message }); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const exportPdf = async (code) => {
    setPdfFor(code);
    try { await downloadPdf(code); } catch (e) { setMsg({ bad: true, text: e.message }); }
    setPdfFor(null);
  };

  const create = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      const j = await send(API, 'POST', { title: title.trim() });
      setAdding(false); setTitle('');
      onOpen(j.code);
    } catch (e) { setMsg({ bad: true, text: e.message }); }
    setBusy(false);
  };

  /* Send for review and Resubmit go through the ONE endpoint the sheet uses. */
  const submit = async (code) => {
    setBusy(true);
    try {
      await send(`${API}/${encodeURIComponent(code)}/review`, 'POST', { action: 'submit' });
      setMsg({ bad: false, text: t('gtm.reviewDone_submit') });
      await load();
    } catch (e) { setMsg({ bad: true, text: e.message }); }
    setBusy(false);
  };

  const inScope = rows.filter((r) => scope === 'all' || r.mine);
  const counts = { draft: 0, in_review: 0, approved: 0, rejected: 0 };
  inScope.forEach((r) => { counts[stateOf(r)] += 1; });
  const q = search.trim().toLowerCase();
  const pre = inScope.filter((r) => (!state || stateOf(r) === state) && matches(r, q));
  const mineN = rows.filter((r) => r.mine).length;
  const [openNote, setOpenNote] = useState(null);

  const askDelete = async (r) => {
    try {
      setBusy(true);
      const pre2 = await get(`${API}/${r.gtm_code}/delete-preflight`);
      setDelRow({ ...pre2, code: r.gtm_code, title: r.title });
    } catch (e) { setMsg({ bad: true, text: e.message }); }
    finally { setBusy(false); }
  };

  const sc = sharedColumns(t, onOpen);
  /* Who wrote it and who touched it last — Max, 2026-10-08: "thiếu cột người tạo, ngày tạo,
     người chỉnh và ngày chỉnh rồi Manager note". "Updated" is stamped on EVERY write under the
     programme, not only its header (the hook at the top of gtm.js), or it would date the title
     and say nothing about the questions. */
  const columns = [
    sc.no, sc.code, sc.title,
    {
      key: 'status', label: t('gtm.colStatus'), get: (r) => t(`gtm.rev_${stateOf(r)}`),
      /* ONE LINE — Max, 2026-10-08: "bỏ cái dòng waiting on Young đi mình chỉ cần 1 dòng status
         thui". Who it is waiting on is already the Approver column on the same row. */
      cell: (r) => <span className={`gtm-review-state ${stateOf(r)}`}>{t(`gtm.rev_${stateOf(r)}`)}</span>,
    },
    sc.decisions, sc.verify, sc.regions, sc.skus, sc.owner, sc.approver, sc.launch,
    { key: 'createdBy', label: t('gtm.colCreatedBy'), get: (r) => r.created_by || '' },
    { key: 'gtmCreatedAt', label: t('gtm.colCreatedAt'), get: (r) => day(r.created_at), td: 'pa-when',
      cell: (r) => stamp(r.created_at) || dash },
    { key: 'updatedBy', label: t('gtm.colUpdatedBy'), get: (r) => r.updated_by || '' },
    { key: 'gtmUpdatedAt', label: t('gtm.colUpdatedAt'), get: (r) => day(r.updated_at), td: 'pa-when',
      cell: (r) => stamp(r.updated_at) || dash },
    {
      /* ONLY WHILE A DECISION STANDS. A resubmitted programme keeps its last decision's note in
         the row, and printing it beside "With the approver" read as a rejection of the version
         now waiting. */
      key: 'managerNote', label: t('gtm.colManagerNote'), get: (r) => (DECIDED.has(stateOf(r)) ? r.decision_note || '' : ''),
      td: 'pa-reason',
      cell: (r) => <NoteCell text={DECIDED.has(stateOf(r)) ? r.decision_note : ''} open={openNote === r.gtm_pk_id}
        onToggle={() => setOpenNote((x) => (x === r.gtm_pk_id ? null : r.gtm_pk_id))} />,
    },
    {
      key: 'action', label: t('gtm.colAction'), th: 'pa-actions-col', td: 'pa-actions-col',
      /* PROJECT APPROVAL'S ROW, the author's half of it: View · Edit · PDF · Submit (or what
         replaced it) · Delete. The green Approve is on the approver's screen and nowhere here. */
      cell: (r) => {
        const st = stateOf(r);
        const locked = DELETE_LOCKED.has(st);
        return (
          <div className="pa-actions">
            <button type="button" className="pa-btn" onClick={() => onOpen(r.gtm_code)}>
              <Eye size={14} /> {t('gtm.view')}
            </button>
            {canEdit && (
              <button type="button" className="pa-btn" onClick={() => onOpen(r.gtm_code, true)}>
                <Pencil size={14} /> {t('gtm.edit')}
              </button>
            )}
            <button type="button" className="pa-btn" disabled={pdfFor === r.gtm_code}
              title={t('gtm.exportPdf')} onClick={() => exportPdf(r.gtm_code)}>
              <FileDown size={14} /> {pdfFor === r.gtm_code ? t('gtm.exporting') : t('gtm.pdf')}
            </button>
            {canEdit && st === 'draft' && (
              <button type="button" className="pa-btn submit" disabled={busy} onClick={() => submit(r.gtm_code)}>
                <Mail size={14} /> {t('gtm.sendForReview')}
              </button>
            )}
            {canEdit && st === 'rejected' && (
              <button type="button" className="pa-btn" disabled={busy} onClick={() => submit(r.gtm_code)}>
                <RotateCcw size={14} /> {t('gtm.resubmit')}
              </button>
            )}
            {st === 'in_review' && (
              <span className="pa-sub-when" title={`${t('gtm.sentForReview')}: ${stamp(r.submitted_at)}`}>
                <Mail size={13} /> {t('gtm.sent')}
              </span>
            )}
            {/* DEAD, NOT HIDDEN, once it has been sent — Max: "nếu đã sent thì nút delete phải
                disable". A button that vanishes leaves somebody hunting for it; a dead one says
                why on its tooltip. */}
            {canEdit && (
              <button type="button" className="pa-btn pa-btn-drop" disabled={busy || locked}
                title={locked ? t(st === 'approved' ? 'gtm.deleteLockedApproved' : 'gtm.deleteLockedSent')
                  : t('gtm.deleteProgramme')}
                onClick={() => askDelete(r)}>
                <Trash2 size={14} /> {t('gtm.delete')}
              </button>
            )}
          </div>
        );
      },
    },
  ];
  const tools = useColumnTools(pre, columns);
  const shown = tools.shown;
  const filtered = !!state || !!q || tools.active;

  /* A function, not a component: declared inside the render, a component would be a new type on
     every render and React would throw the card away and build it again each time. */
  const card = (k, label, value, tone) => (
    <button type="button" key={k || 'all'}
      className={`gtm-kpi ${tone || ''} ${state === k ? 'active' : ''}`}
      aria-pressed={state === k}
      onClick={() => setState((cur) => (cur === k ? '' : k))}>
      <div className="kl">{label}</div>
      <div className="kv">{value}</div>
    </button>
  );

  return (
    <div className="gtm-listpage">
      {delRow && (
        <GtmDeleteDialog t={t} code={delRow.code} title={delRow.title}
          rows={delRow.rows} photos={delRow.photos} busy={busy}
          onCancel={() => setDelRow(null)}
          onConfirm={async () => {
            setBusy(true);
            try {
              await send(`${API}/${delRow.code}`, 'DELETE');
              setDelRow(null);
              await load();
            } catch (e) { setMsg({ bad: true, text: e.message }); }
            finally { setBusy(false); }
          }} />
      )}

      <div className="mh-titlerow">
        <div className="pa-title">
          <h2>{t('gtm.title')}</h2>
          <p>{t('gtm.subtitle')}</p>
        </div>
        <ModuleHelp manual="gtm" steps={TOUR_PROGRAMMES} tourKey="gtmProgrammes" ready={!loading}
          onBeforeTour={() => { setScope('mine'); setAdding(false); }} />
      </div>

      {msg && <div className={`pa-banner ${msg.bad ? 'err' : 'ok'}`}>{msg.text}</div>}

      {/* WHOSE PROGRAMMES — Project Approval's tabs, because "mine or everybody's" is the one
          choice that changes every figure below it, cards included. */}
      <div className="pa-tabs gtm-scope">
        <button type="button" className={`pa-tab ${scope === 'mine' ? 'active' : ''}`}
          onClick={() => { setScope('mine'); setState(''); tools.clear(); }}>
          {t('gtm.scopeMine')} <span className="pa-count">{mineN}</span>
        </button>
        <button type="button" className={`pa-tab ${scope === 'all' ? 'active' : ''}`}
          onClick={() => { setScope('all'); setState(''); tools.clear(); }}>
          {t('gtm.scopeAll')} <span className="pa-count">{rows.length}</span>
        </button>
      </div>

      <div className="gtm-kpis">
        {card('', scope === 'mine' ? t('gtm.kpiMine') : t('gtm.kpiAll'), inScope.length)}
        {REVIEW_STATES.map((s) => card(s, t(`gtm.rev_${s}`), counts[s], STATE_TONE[s]))}
      </div>

      {adding && (
        <div className="pa-section">
          <h3 className="pa-sec-title"><Plus size={15} /> {t('gtm.createTitle')}</h3>
          <div className="gtm-create">
            <label>
              <span>{t('gtm.nameLabel')}</span>
              <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') create(); }}
                placeholder={t('gtm.namePlaceholder')} />
            </label>
            <button type="button" className="pa-btn primary" onClick={create} disabled={busy || !title.trim()}>
              {t('gtm.create')}
            </button>
            <button type="button" className="pa-btn" onClick={() => { setAdding(false); setTitle(''); }}>
              {t('gtm.cancel')}
            </button>
          </div>
        </div>
      )}

      <div className="pa-section">
        <div className="pa-toolbar">
          <span className="pa-count-text">
            {filtered ? t('gtm.countOf', { n: shown.length, total: inScope.length })
              : t('gtm.countAll', { count: inScope.length })}
          </span>
          <input className="gtm-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder={t('gtm.searchPh')} />
          {filtered && (
            /* Clear takes the funnels and the sort with it — Ticket Station's Clear once left a
               column filter narrowing the grid with nothing on screen to say so. */
            <button type="button" className="pa-btn" onClick={() => { setState(''); setSearch(''); tools.clear(); }}>
              {t('gtm.clear')}
            </button>
          )}
          <span style={{ flex: 1 }} />
          {canEdit && !adding && (
            <button type="button" className="pa-btn primary" onClick={() => setAdding(true)}>
              <Plus size={14} /> {t('gtm.newProgramme')}
            </button>
          )}
        </div>

        {loading && <div className="pa-empty"><Loader2 size={16} className="pa-spin" /> {t('gtm.loading')}</div>}
        {!loading && shown.length === 0 && (
          <div className="pa-empty">
            {scope === 'mine' && !inScope.length
              ? (
                <>
                  {t('gtm.noneMine')}
                  {rows.length > 0 && (
                    <button type="button" className="pa-btn" onClick={() => setScope('all')}>
                      {t('gtm.showAll', { count: rows.length })}
                    </button>
                  )}
                </>
              )
              : (rows.length ? t('gtm.noMatch') : t('gtm.none'))}
          </div>
        )}
        {!loading && shown.length > 0 && <GtmTable columns={columns} tools={tools} />}
      </div>
    </div>
  );
}

/* ---- GTM › Approval: the approver's queue ------------------------------------------------------ */
/* PROJECT APPROVAL'S SCREEN, for a programme. Three tabs because the approver's questions are
   three: what is waiting on me, what I agreed to, what I sent back. Drafts never appear — they are
   with the person writing them — and neither does anybody else's queue unless this person is an
   admin, which the subtitle then says. */
const APPROVAL_TABS = [
  { key: 'in_review', label: 'gtm.tabWaiting' },
  { key: 'approved', label: 'gtm.tabApproved' },
  { key: 'rejected', label: 'gtm.tabRejected' },
];

function ApprovalList({ t, onOpen }) {
  const [rows, setRows] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pdfFor, setPdfFor] = useState(null);
  const [tab, setTab] = useState('in_review');
  const [search, setSearch] = useState('');
  const [decisionFor, setDecisionFor] = useState(null);
  const [revNote, setRevNote] = useState('');

  const load = useCallback(async () => {
    try {
      const j = await get(`${BACKEND_URL}/api/gtm-approval`);
      setRows(j.rows || []);
      setIsAdmin(!!j.isAdmin);
    } catch (e) { setMsg({ bad: true, text: e.message }); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const exportPdf = async (code) => {
    setPdfFor(code);
    try { await downloadPdf(code); } catch (e) { setMsg({ bad: true, text: e.message }); }
    setPdfFor(null);
  };

  const decide = async (code, action, note) => {
    setBusy(true);
    try {
      await send(`${API}/${encodeURIComponent(code)}/review`, 'POST', { action, note: String(note || '').trim() });
      setDecisionFor(null); setRevNote('');
      setMsg({ bad: false, text: t(`gtm.reviewDone_${action}`) });
      await load();
    } catch (e) {
      /* The dialog CLOSES on a refusal — left open, its overlay sat over the banner that said why,
         and the press looked as though it had done nothing. */
      setDecisionFor(null);
      setMsg({ bad: true, text: e.message });
    }
    setBusy(false);
  };

  const count = (k) => rows.filter((r) => r.status === k).length;
  const q = search.trim().toLowerCase();
  const inTab = rows.filter((r) => r.status === tab);
  const decided = tab !== 'in_review';
  const [openNote, setOpenNote] = useState(null);

  const sc = sharedColumns(t, onOpen);
  const columns = [
    sc.no, sc.code, sc.title,
    { key: 'sentBy', label: t('gtm.colSentBy'), get: (r) => r.submitted_by || '' },
    { key: 'gtmSentOn', label: t('gtm.colSentOn'), get: (r) => day(r.submitted_at), td: 'pa-when',
      cell: (r) => stamp(r.submitted_at) || dash },
    /* Whose decision it is, printed only where it can differ from the reader: an admin's queue
       holds every approver's programmes. */
    ...(isAdmin ? [sc.approver] : []),
    ...(decided ? [
      { key: 'gtmDecidedOn', label: t('gtm.colDecidedOn'), get: (r) => day(r.decided_at), td: 'pa-when',
        cell: (r) => stamp(r.decided_at) || dash },
      {
        key: 'managerNote', label: t('gtm.colManagerNote'), get: (r) => r.decision_note || '', td: 'pa-reason',
        cell: (r) => <NoteCell text={r.decision_note} open={openNote === r.gtm_pk_id}
          onToggle={() => setOpenNote((x) => (x === r.gtm_pk_id ? null : r.gtm_pk_id))} />,
      },
    ] : []),
    sc.decisions, sc.verify, sc.regions, sc.skus, sc.launch,
    /* Who touched it last — on the approver's screen this is the line that says the sheet moved
       after it was sent to him. */
    { key: 'updatedBy', label: t('gtm.colUpdatedBy'), get: (r) => r.updated_by || '' },
    { key: 'gtmUpdatedAt', label: t('gtm.colUpdatedAt'), get: (r) => day(r.updated_at), td: 'pa-when',
      cell: (r) => stamp(r.updated_at) || dash },
    {
      key: 'action', label: t('gtm.colAction'), th: 'pa-actions-col', td: 'pa-actions-col',
      /* View · PDF · Approve — the approver's half of Project Approval's row. Nothing here
         changes the sheet. */
      cell: (r) => (
        <div className="pa-actions">
          <button type="button" className="pa-btn" onClick={() => onOpen(r.gtm_code)}>
            <Eye size={14} /> {t('gtm.view')}
          </button>
          <button type="button" className="pa-btn" disabled={pdfFor === r.gtm_code}
            title={t('gtm.exportPdf')} onClick={() => exportPdf(r.gtm_code)}>
            <FileDown size={14} /> {pdfFor === r.gtm_code ? t('gtm.exporting') : t('gtm.pdf')}
          </button>
          {r.canDecide && (
            <button type="button" className="pa-btn approve" disabled={busy}
              onClick={() => { setRevNote(''); setDecisionFor({ action: 'approve', code: r.gtm_code, title: r.title }); }}>
              <CheckCircle2 size={14} /> {t('gtm.approve')}
            </button>
          )}
        </div>
      ),
    },
  ];
  const tools = useColumnTools(inTab.filter((r) => matches(r, q)), columns);
  const shown = tools.shown;
  const filtered = !!q || tools.active;

  return (
    <div className="gtm-listpage">
      {decisionFor && (
        <DecisionDialog
          spec={{ action: decisionFor.action, group: { groupId: decisionFor.code, groupName: decisionFor.title } }}
          note={revNote}
          onNote={setRevNote}
          busy={busy}
          selectedIds={[]}
          onSwitch={(action) => setDecisionFor((x) => ({ ...x, action }))}
          onCancel={() => { setDecisionFor(null); setRevNote(''); }}
          onConfirm={() => decide(decisionFor.code, decisionFor.action, revNote)}
        />
      )}

      <div className="mh-titlerow">
        <div className="pa-title">
          <h2>{t('gtm.approvalTitle')}</h2>
          <p>{isAdmin ? t('gtm.approvalSubAdmin') : t('gtm.approvalSub')}</p>
        </div>
        <ModuleHelp manual="gtm" steps={TOUR_APPROVAL} tourKey="gtmApproval" ready={!loading}
          onBeforeTour={() => setTab('in_review')} />
      </div>

      {msg && <div className={`pa-banner ${msg.bad ? 'err' : 'ok'}`}>{msg.text}</div>}

      <div className="pa-tabs">
        {APPROVAL_TABS.map((tb) => (
          /* Switching tab resets the column tools, as Project Approval's does — the rows are
             different, and a funnel set on another tab's columns would filter by nothing visible. */
          <button key={tb.key} type="button" className={`pa-tab ${tab === tb.key ? 'active' : ''}`}
            onClick={() => { setTab(tb.key); tools.clear(); }}>
            {t(tb.label)} <span className="pa-count">{count(tb.key)}</span>
          </button>
        ))}
      </div>

      <div className="pa-section">
        <div className="pa-toolbar">
          <span className="pa-count-text">
            {filtered ? t('gtm.countOf', { n: shown.length, total: inTab.length })
              : t('gtm.countAll', { count: inTab.length })}
          </span>
          <input className="gtm-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder={t('gtm.searchPh')} />
          {filtered && (
            <button type="button" className="pa-btn" onClick={() => { setSearch(''); tools.clear(); }}>{t('gtm.clear')}</button>
          )}
        </div>

        {loading && <div className="pa-empty"><Loader2 size={16} className="pa-spin" /> {t('gtm.loading')}</div>}
        {!loading && shown.length === 0 && (
          <div className="pa-empty">{filtered ? t('gtm.noMatch') : t(`gtm.emptyQueue_${tab}`)}</div>
        )}
        {!loading && shown.length > 0 && <GtmTable columns={columns} tools={tools} extraClass="gtm-atable" />}
      </div>
    </div>
  );
}

/* ---- one open item ------------------------------------------------------------------------- */
function OpenItem({ t, item, canEdit, onEdit, onSave, onRemove }) {
  const [answering, setAnswering] = useState(false);
  const [answer, setAnswer] = useState('');
  const done = item.status !== 'open';
  /* THE ROW READS, THE FORM WRITES — Max, 2026-10-07: "sao tui thấy bạn ko xây dựng cái GTM này
     như 1 cái module input vậy". Owner and Due used to be click-to-edit here, which was a third
     way of putting a value in on one page. They are plain text now and the pencil opens the same
     form every other row on this screen opens, with the kind, the detail and the phase beside
     them — the fields that could only ever be set while CREATING the item. */
  return (
    <div className={`gtm-item${done ? ' done' : ''}`}>
      <span className={`gtm-kind ${item.kind}`}>{t(`gtm.${item.kind}`)}</span>
      <div className="gtm-itemmain">
        <div className="t">{item.title}</div>
        {item.detail && <div className="d">{item.detail}</div>}
        <div className="gtm-itemmeta">
          {/* The phase, where it belongs — on the row, not only in the block above. Somebody
              reading a question needs to know whether this is a thing for before launch or for
              after it without scrolling back up. */}
          {item.phase && <span className={`gtm-phase ${item.phase}`}>{t(`gtm.phase_${item.phase}`)}</span>}
          <span>{t('gtm.owner')}: <b>{item.owner_name || t('gtm.noOwner')}</b></span>
          <span>{t('gtm.due')}: <b>{day(item.due_date) || t('gtm.noDue')}</b></span>
          {done && <span>{t(`gtm.${item.status === 'answered' ? 'answered' : 'dropped'}`)}
            {item.answered_by ? ` · ${item.answered_by}` : ''}</span>}
        </div>
        {item.answer && <div className="gtm-ans">{item.answer}</div>}
        {answering && (
          <div style={{ marginTop: 7 }}>
            <textarea
              value={answer} onChange={(e) => setAnswer(e.target.value)} rows={3}
              style={{ width: '100%', font: 'inherit', border: '1px solid #cbd5e1',
                borderRadius: 6, padding: 8 }}
            />
            <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
              <button
                type="button" className="gtm-btn small primary"
                onClick={() => { onSave({ id: item.item_pk_id, status: 'answered', answer }); setAnswering(false); }}
              >
                {t('gtm.save')}
              </button>
              <button type="button" className="gtm-btn small" onClick={() => setAnswering(false)}>
                {t('gtm.cancel')}
              </button>
            </div>
          </div>
        )}
      </div>
      {canEdit && (
        <div className="gtm-itemacts">
        {canEdit && (
          <button type="button" className="gtm-btn small" title={t('gtm.edit')}
            onClick={() => onEdit(item)}>
            <Pencil size={12} />
          </button>
        )}
          {!done && !answering && (
            <button type="button" className="gtm-btn small" onClick={() => setAnswering(true)}>
              <Check size={12} style={{ verticalAlign: '-2px' }} /> {t('gtm.answer')}
            </button>
          )}
          {done && (
            <button
              type="button" className="gtm-btn small"
              onClick={() => onSave({ id: item.item_pk_id, status: 'open' })}
            >
              {t('gtm.open')}
            </button>
          )}
          <button type="button" className="gtm-btn small" title={t('gtm.remove')}
            onClick={() => onRemove(item.item_pk_id)}>
            <Trash2 size={12} />
          </button>
        </div>
      )}
    </div>
  );
}

/* ---- the programme ---------------------------------------------------------------------------- */
/* `mode` is WHICH SCREEN the sheet was opened from, and it decides what the sheet offers —
   Max, 2026-10-08: "hiện giao diện của người tạo và người approve ko tách ra". From the author's
   list ('author') it is the workspace: Edit, Send for review, Delete, and no Approve. From the
   approver's queue ('approval') it is a document to decide on: read-only, Approve, and nothing
   that changes the sheet. The SERVER still enforces both halves; this decides what is painted, so
   somebody who holds both roles is never shown both sets of buttons on one screen. */
function Programme({ code, t, onBack, mode = 'author' }) {
  const approval = mode === 'approval';
  const [d, setD] = useState(null);
  const [msg, setMsg] = useState(null);
  /* EVERY SECTION IS OPEN, AND THE PAGE HAS ONE SAVE — Max, 2026-10-07: "ko cần chỗ nào điền thì
     có nút save đâu, tôi đang hiểu là user để khi tạo sẽ điền hết các thông tin rùi lưu 1 lần á".
     It was click-to-edit with a Save and a Cancel under each of the seven, which is the shape for
     amending one paragraph of a living document and the wrong shape for the moment that matters:
     somebody sitting down with the masterplan and filling the whole thing in.

     `drafts` holds ONLY what has been typed, keyed by section id — not a copy of every section.
     That is what lets the programme reload underneath without wiping an edit in progress: an
     untouched section reads from the server's value, a touched one from here. */
  const [drafts, setDrafts] = useState({});
  /* ONE FORM, ONE PIECE OF STATE. `{ kind, value }` — kind picks the field list and where the
     save goes, value is the row being edited or the seed for a new one. Five separate `adding`
     and `editing` flags were what made this screen feel like five screens. */
  /* NOTHING OPENS OVER THE TOP — Max, 2026-10-07: "ko cần pop up nha cho điền trực tiếp luôn là
     add line thì sẽ hiện ra các trường cần điền trên dòng đó". `itemEdit` is the row being typed:
     `{ q_no }` for a new line, the row itself for an edit, so adding and editing are one
     component and cannot drift apart. `headEdit` is the same idea for the programme's own
     header. */
  const [itemEdit, setItemEdit] = useState(null);
  const [headEdit, setHeadEdit] = useState(null);
  /* THE DECISION IS TAKEN IN THE PORTAL'S OWN DIALOG — Max, 2026-10-08: "tham khảo Project
     approval nha". It was an inline textarea with Approve and Send back beside it, which is a
     fourth way to take a decision in a portal that already has one: `DecisionDialog` is what
     Project Approval, Pricing Approval and the pricing calculator all open, it carries the
     Agree/Reject switch inside it so somebody who opens Approve and changes their mind does not
     have to close it and find the other button, and it already enforces "a note is required to
     reject".
     THIS IS NOT A REVERSAL OF "ko cần pop up nha" (2026-10-07). That was about ENTERING DATA —
     add a line, edit a row — where a dialog over the sheet hid the thing being edited. A decision
     is not data entry: it is one act, taken once, that stops the sheet being editable. */
  const [decisionFor, setDecisionFor] = useState(null);
  const [revNote, setRevNote] = useState('');
  /* What the delete is about to take, fetched before the sentence is drawn. null = not asking. */
  const [delAsk, setDelAsk] = useState(null);
  /* The export takes a few seconds — a browser is started for it — so the button says so rather
     than looking as though the click missed. */
  const [pdfBusy, setPdfBusy] = useState(false);
  /* TWO THINGS THE URL CAN ASK FOR, and both exist because something OUTSIDE this screen needs
     them: `edit=1` is the list's Edit button, which has no sheet to unlock from over there, and
     `print=1` is the PDF renderer, which drives a real browser and needs the document state
     (locked, every question open) without a human pressing anything. Read ONCE on arrival —
     re-reading would re-lock the sheet under somebody who had since pressed Cancel. */
  /* WHERE THE FROZEN BAR HAS TO STOP, MEASURED — not declared. `.gtm-bar` stuck at `top: 0` and
     landed UNDERNEATH the portal's own header, which is `position: fixed`, 58px tall and z-index
     1000: present in the DOM, scrolled to the right place, and completely covered. Found by asking
     what is PAINTED at the bar's own centre (`elementFromPoint` returned `header-container`), not
     by looking at it — the seventh time this codebase has lost a control to something drawn on top
     of it, and the first where a check on "is it sticky" would have passed.
     MEASURED because `.header-container` declares no height at all — it is padding plus whatever
     is in it, so there is no token to read and a hard-coded 58 would be a number that stops being
     true the day somebody adds a row to the header. Same rule `useFrozenColumns` keeps about
     declared column widths. Re-measured on resize, since the header wraps on a narrow window. */
  useEffect(() => {
    const fit = () => {
      const h = document.querySelector('.header-container')?.getBoundingClientRect().height || 0;
      document.documentElement.style.setProperty('--gtm-top', `${Math.round(h)}px`);
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);

  const askedRef = useRef(false);
  useEffect(() => {
    if (askedRef.current || !d) return;
    askedRef.current = true;
    const q = new URLSearchParams(window.location.search);
    /* FOLDED ON ARRIVAL — Max, 2026-10-08: "mặc định là collapse all nhé". The sheet is eight
       blocks and four tables deep; open, the first screen is the top of question 1 and the shape
       of the whole programme is three scrolls away. Folded, the first screen IS the programme —
       seven questions with their open counts beside them — and a reader opens the one they came
       for. Expand all is one press.
       `print=1` OVERRIDES it, because a PDF of seven folded headings is a PDF of nothing; that
       branch runs first for exactly this reason. */
    if (q.get('print') === '1') { setEditing(false); setHeadEdit(null); setShut(new Set()); }
    else if (q.get('edit') === '1' && d.canEdit && !approval) {
      const pr = d.programme || {};
      setHeadEdit({
        title: pr.title || '', headline: pr.headline || '',
        owner_name: pr.owner_name || '',
        launch_target: (pr.launch_target || '').toString().slice(0, 10),
      });
      setEditing(true);
      /* Editing is the one case that does NOT fold: somebody who pressed Edit on the list came
         to change something, and handing them seven closed headings would make them open every
         one before they could start. */
    } else {
      setShut(new Set([0, ...(d.questions || []).map((x) => x.q)]));
    }
    // `approval` is fixed for the life of the sheet (it is the route), and the ref keeps this to once.
  }, [d, approval]);
  /* The stop condition being written. `manual` is the default because it is the one that works on
     any programme; the two evaluated kinds are shapes this document happens to have. */
  /* `rule_kind` is no longer asked for: a new condition is HAND-WATCHED, which is the only
     honest answer when nobody chose an evaluator. It is still SENT, because the endpoint
     validates the column against the three values it knows. */
  const [stopNew, setStopNew] = useState({ statement: '', note: '' });
  const addStop = async () => {
    const statement = stopNew.statement.trim();
    if (!statement) return;
    setBusy(true);
    try {
      await send(`${API}/${code}/line/rule`, 'POST', {
        label: statement,
        rule_kind: 'manual',
        note: stopNew.note,
      });
      setStopNew({ statement: '', note: '' });
      await load();
    } catch (e) { say(e.message, true); } finally { setBusy(false); }
  };
  /* WHICH QUESTIONS ARE SHUT — Max, 2026-10-07: "mỗi section câu hỏi có thể expand và collapse".
     Seven questions each carrying tables and a list make a long sheet; folding one is how
     somebody gets to Q6 without scrolling past Q2's four tables.
     A SET OF THE SHUT ONES, not of the open ones: a question added later is open by default,
     which is what a document should do. Held in the page, not stored — it is where a reader has
     got to, not a fact about the programme. */
  const [shut, setShut] = useState(() => new Set());
  const toggleQ = (n) => setShut((prev) => {
    const next = new Set(prev);
    if (next.has(n)) next.delete(n); else next.add(n);
    return next;
  });
  /* ONE EDIT FOR THE WHOLE SHEET — Max, 2026-10-07: "rất là nhiều lỗi UI / tôi muốn nút edit
     apply cho toàn bộ phiếu". Walked as a user, this page was showing FIVE different answers to
     "can I change this right now": the prose carried a formatting toolbar on every question, the
     layer and region tables were a wall of input boxes, the ranked lists needed their own "Edit
     the table", an item had a pencil and so did the header. Locked is now the whole page's state
     and so is unlocked; nothing has a lock of its own.
     `rowEdits` is the draft for every table and list cell, keyed `kind:id:field`, so one Save
     writes the lot and Cancel drops it. Dragging stays immediate — the row moving IS the
     feedback — and the bar says so rather than claiming otherwise. */
  const [editing, setEditing] = useState(false);
  const [rowEdits, setRowEdits] = useState({});
  /* A REF, NOT STATE. The lists report their drafts on every render; writing that into state
     would re-render them and report again. Nothing reads it until Save is pressed. */
  const listEdits = useRef({});
  const cellOf = (kind, row, pk, field) => {
    const k = `${kind}:${row[pk]}:${field}`;
    return k in rowEdits ? rowEdits[k] : (row[field] ?? '');
  };
  /* An item row's draft is the stored row with whatever has been typed over it — so a field
     nobody touched still shows what is there, and the Save sends the whole row. */
  const itemDraft = (i) => ({
    kind: cellOf('item', i, 'item_pk_id', 'kind') || 'todo',
    title: cellOf('item', i, 'item_pk_id', 'title'),
    detail: cellOf('item', i, 'item_pk_id', 'detail'),
    owner_name: cellOf('item', i, 'item_pk_id', 'owner_name'),
    due_date: String(cellOf('item', i, 'item_pk_id', 'due_date') || '').slice(0, 10),
    phase: cellOf('item', i, 'item_pk_id', 'phase'),
  });
  const cellSet = (kind, id, field, v) =>
    setRowEdits((prev) => ({ ...prev, [`${kind}:${id}:${field}`]: v }));
  /* THE PROGRAMME'S OWN FIELDS, edited in place rather than in the header dialog. Q5's two column
     headings are programme columns, and they are read and typed where they are printed — at the
     top of the table they name. They ride `rowEdits` under the kind `prog` so the one Save at the
     foot of the sheet carries them with everything else; `0` stands in for the id, because there
     is one programme on this page and it is the one the page is of. */
  const progOf = (field) => {
    const k = `prog:0:${field}`;
    return k in rowEdits ? rowEdits[k] : (d?.programme?.[field] ?? '');
  };
  const progSet = (field, v) => cellSet('prog', 0, field, v);
  /* A photo's caption is the same shape of edit: typed while the sheet is unlocked, saved with
     the rest. Keyed by the photo id, so two captions are two writes and not one overwriting the
     other. */
  /* THE STORED CAPTION IS READ OUT OF THE PROGRAMME, not off the photo object handed in.
     <ProjectPhotos> fetches its own list once and keeps it; `load()` after a save refreshes the
     PROGRAMME, so a caption read from the component's copy was the value as it stood when the
     page opened — saved correctly and then drawn as blank, which is how it first behaved. */
  const capOf = (ph) => {
    const k = `photo:${ph.photoId}:caption`;
    if (k in rowEdits) return rowEdits[k];
    const mine = (d?.photos || []).find((x) => x.photoId === ph.photoId);
    return (mine ? mine.caption : ph.caption) || '';
  };
  /* Fetched once for the whole programme, not per form: the list is the same for every item and
     one request beats sixteen. A failure leaves it empty, which costs the suggestions and nothing
     else — the field still takes a typed name. */
  const [people, setPeople] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setD(await get(`${API}/${code}`)); }
    catch (e) { setMsg({ bad: true, text: e.message }); }
  }, [code]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let gone = false;
    fetch(`${BACKEND_URL}/api/gtm/lookup/people`, { credentials: 'include' })
      .then((r) => r.json())
      .then((b) => { if (!gone) setPeople(b.rows || []); })
      .catch(() => { /* suggestions only */ });
    return () => { gone = true; };
  }, []);

  /* THE SHARED LISTS every programme picks personas, channels and regions from — Max, 2026-10-16:
     "select từ những cái có sẵn ... type giá trị mới và add vào để lần sau dùng", then "Regions,
     channel cũng vậy". Read again after an add, because a new name has just joined its list. */
  const [lists, setLists] = useState({ persona: [], channel: [], region: [] });
  const loadLists = useCallback(() => {
    fetch(`${BACKEND_URL}/api/gtm/lookup/catalog`, { credentials: 'include' })
      .then((r) => r.json())
      .then((b) => setLists({
        persona: (b.persona || []).map((x) => x.name),
        channel: (b.channel || []).map((x) => x.name),
        region: (b.region || []).map((x) => x.name),
      }))
      .catch(() => { /* suggestions only */ });
  }, []);
  useEffect(() => { loadLists(); }, [loadLists]);

  const say = (text, bad) => { setMsg({ bad, text }); setTimeout(() => setMsg(null), 4000); };

  /* WHAT HAS ACTUALLY CHANGED, not what has been touched. Typing a word and deleting it again
     leaves a draft entry that equals the stored body, and counting that would offer to save
     nothing and then report "Saved". */
  const changed = Object.entries(drafts).filter(([id, v]) => {
    const sec = (d?.sections || []).find((x) => String(x.section_pk_id) === String(id));
    return sec && v !== (sec.body || '');
  });

  const saveAll = async () => {
    if (!changed.length) return;
    setBusy(true);
    try {
      /* One at a time, not in parallel: the endpoint reloads the programme after each write and
         two in flight race to tell the screen what the programme now is. Seven sections is seven
         quick calls, and nobody is waiting on a spinner they cannot see. */
      for (const [id, body] of changed) {
        await send(`${API}/${code}/section`, 'POST', { id: Number(id), body });
      }
      setDrafts({});
      await load();
      say(t('gtm.saved'));
    } catch (e) { say(e.message, true); }
    finally { setBusy(false); }
  };
  /* REVIEW AND APPROVAL — Max, 2026-10-08: "Add a review and approval function. Current
     approver: Young." One writer for all three acts, because they are one endpoint and the
     difference between them is a word it validates, not a different request. */
  const review = async (action, note) => {
    setBusy(true);
    try {
      /* The server is the one that REQUIRES a note on a rejection — the dialog asks for it, but a
         rule that lives in the UI is not a rule. */
      await send(`${API}/${code}/review`, 'POST', { action, note: String(note || '').trim() });
      setRevNote('');
      setDecisionFor(null);
      await load();
      say(t(`gtm.reviewDone_${action}`));
    } catch (e) { setDecisionFor(null); say(e.message, true); }
    setBusy(false);
  };
  const child = async (kind, body) => {
    setBusy(true);
    try { await send(`${API}/${code}/${kind}`, 'POST', body); await load(); say(t('gtm.saved')); }
    catch (e) { say(e.message, true); }
    setBusy(false);
  };
  /* ADD AND REMOVE A LINE on Q5's table and Q6's three sections. Their own endpoints, because
     they are not ranked lists — nothing drags them and a new line goes to the bottom of its own
     section rather than to a position somebody has to then correct. */
  const addLine = async (list, body) => {
    setBusy(true);
    try { await send(`${API}/${code}/line/${list}`, 'POST', body); await load(); }
    catch (e) { say(e.message, true); }
    setBusy(false);
  };
  const dropLine = async (list, id) => {
    setBusy(true);
    try { await send(`${API}/${code}/line/${list}/${id}`, 'DELETE'); await load(); }
    catch (e) { say(e.message, true); }
    setBusy(false);
  };
  const dropChild = async (kind, id) => {
    setBusy(true);
    try { await send(`${API}/${code}/${kind}/${id}`, 'DELETE'); await load(); }
    catch (e) { say(e.message, true); }
    setBusy(false);
  };

  const byQuestion = useMemo(() => {
    const m = new Map();
    (d?.sections || []).forEach((s) => {
      if (!m.has(s.q_no)) m.set(s.q_no, { sections: [], items: [] });
      m.get(s.q_no).sections.push(s);
    });
    (d?.items || []).forEach((i) => {
      if (i.q_no === null || i.q_no === undefined) return;
      if (!m.has(i.q_no)) m.set(i.q_no, { sections: [], items: [] });
      m.get(i.q_no).items.push(i);
    });
    return m;
  }, [d]);

  /* Items with no question hang on the programme itself — the document has some ("Owners — author
     outreach and the 60-7 event"), and dropping them into question 1 would file them under an
     answer they have nothing to do with. */
  const loose = useMemo(
    () => (d?.items || []).filter((i) => i.q_no === null || i.q_no === undefined), [d],
  );

  if (!d) {
    return (
      <div className="gtm-page">
        <button type="button" className="pa-btn" onClick={onBack}>
          <ArrowLeft size={14} /> {approval ? t('gtm.backApproval') : t('gtm.back')}
        </button>
        {msg && <div className={`gtm-msg ${msg.bad ? 'bad' : 'ok'}`} style={{ marginTop: 12 }}>{msg.text}</div>}
      </div>
    );
  }

  const p = d.programme;
  /* The approver's view never edits, whatever the grant says — an admin deciding on a sheet is
     deciding on what was SENT, and an Edit button beside Approve is the mixed screen this split
     exists to end. */
  const canEdit = !approval && !!d.canEdit;
  /* `canEdit` is whether this person MAY edit; `may` is whether the sheet is unlocked right now.
     Every editor on the page reads the second, and only the Edit button reads the first. */
  const may = canEdit && editing;



  /* ONE WRITER FOR BOTH RANKED LISTS, matching the one endpoint behind them. It reloads rather
     than patching the row in place: the rank of every row below the one moved has changed too, and
     re-deriving those on the screen would be a second implementation of the thing the server just
     did. */
  const saveOrder = async (list, order) => {
    setBusy(true);
    try {
      await send(`${API}/${encodeURIComponent(code)}/${list}/order`, 'PUT', { order });
      await load();
    } catch (e) {
      /* The screen is already showing the new order — the drag moved it. Saying so and reloading
         is the only honest answer: leaving it there would be the page claiming a save that did
         not happen, which is the fault the Master Sheet hit when a stale list clobbered an edit. */
      say(e.message, true);
      await load();
    } finally { setBusy(false); }
  };

  /* ONE SAVE FOR THE SHEET. It walks what was drafted — the seven prose sections, the two
     tables' cells, the three lists' cells — and reports what it wrote. SEQUENTIAL, not one
     transaction across six tables, and the bar says "Saved n changes" rather than implying
     otherwise; each list's own write is still atomic within itself.
     DRAGGING IS NOT IN HERE and never was: moving a row is direct manipulation, the row moving
     IS the confirmation, and it writes as it happens. */
  const pendingCount = () => {
    const lists = Object.values(listEdits.current).reduce((n, a) => n + (a?.length || 0), 0);
    const cells = new Set(Object.keys(rowEdits).map((k) => k.split(':').slice(0, 2).join(':'))).size;
    return changed.length + lists + cells;
  };

  const saveSheet = async (headerSaved) => {
    setBusy(true);
    /* The header counts. It saved a moment ago through its own call, and leaving it out made the
       banner read "Saved 2" after three things were typed — a count that is nearly right is the
       kind that stops being read. */
    let n = headerSaved ? 1 : 0;
    try {
      for (const [id, body] of changed) {
        await send(`${API}/${code}/section`, 'POST', { id: Number(id), body }); n += 1;
      }
      /* The cells are keyed `kind:id:field`, so they are gathered back into one body per ROW —
         three columns of one region are one write, not three. */
      const byRow = {};
      for (const [k, v] of Object.entries(rowEdits)) {
        const [kind, id, field] = k.split(':');
        (byRow[`${kind}:${id}`] = byRow[`${kind}:${id}`] || { kind, id: Number(id) })[field] = v;
      }
      for (const body of Object.values(byRow)) {
        const { kind, ...rest } = body;
        /* An item carries a rule the other tables do not: only a to-do has a phase, so a row
           whose kind was changed away from 'todo' must drop it rather than keep a moment it no
           longer belongs to. The row is sent whole, since the endpoint takes a whole row. */
        if (kind === 'item') {
          const row = (d.items || []).find((x) => x.item_pk_id === rest.id) || {};
          const full = { ...itemDraft(row), ...rest };
          await send(`${API}/${code}/item`, 'POST', {
            id: rest.id, kind: full.kind,
            title: String(full.title || '').trim(),
            detail: full.detail || null,
            owner_name: full.owner_name || null,
            due_date: full.due_date || null,
            phase: full.kind === 'todo' ? (full.phase || null) : null,
          });
          n += 1;
          continue;
        }
        /* The programme's own fields go to the programme, not to a child table of that name. */
        if (kind === 'prog') {
          const { id, ...fields } = rest;
          await send(`${API}/${code}`, 'PUT', fields); n += 1;
          continue;
        }
        /* A caption is on the shared photo store, which has its own route and its own gate. */
        if (kind === 'photo') {
          await send(`/api/development/photos/file/${rest.id}/caption`, 'PUT',
            { caption: rest.caption || '' });
          n += 1;
          continue;
        }
        await send(`${API}/${code}/${kind}`, 'POST', rest); n += 1;
      }
      for (const [list, rows] of Object.entries(listEdits.current)) {
        if (!rows || !rows.length) continue;
        await send(`${API}/${encodeURIComponent(code)}/${list}/rows`, 'PUT', { rows });
        n += rows.length;
      }
      setDrafts({}); setRowEdits({}); listEdits.current = {};
      setHeadEdit(null); setEditing(false);
      await load();
      say(n ? t('gtm.savedN', { n }) : t('gtm.savedNothing'));
    } catch (e) {
      /* It stops at the first failure and says so, with what did land — "Saved" over a half-written
         sheet is the one answer nobody can act on. */
      say(t('xGtm.savedBefore', { error: e.message, n }), true);
      await load();
    } finally { setBusy(false); }
  };

  const cancelSheet = () => {
    if (pendingCount() && !window.confirm(t('gtm.discardAsk'))) return;
    setDrafts({}); setRowEdits({}); listEdits.current = {};
    setItemEdit(null);
    setEditing(false);
  };

  const addRow = async (list, body) => {
    setBusy(true);
    try {
      await send(`${API}/${encodeURIComponent(code)}/${list}/row`, 'POST', body);
      await load();
    } catch (e) { say(e.message, true); } finally { setBusy(false); }
  };


  /* Called only by the sheet's own Save, which owns the busy flag, the reload and the lock. */
  const saveHead = async () => {
    try {
      await send(`${API}/${encodeURIComponent(code)}`, 'PUT', {
        title: headEdit.title, headline: headEdit.headline,
        owner_name: headEdit.owner_name.trim() || null,
        launch_target: headEdit.launch_target || null,
      });
      setHeadEdit(null);
    } catch (e) { say(e.message, true); throw e; }
  };

  /* ADD AND EDIT ARE ONE WRITE: the row's own id decides which it is, so neither the row nor
     this has to carry a mode. */
  const saveItem = async (body) => {
    setBusy(true);
    try {
      await send(`${API}/${encodeURIComponent(code)}/item`, 'POST', {
        id: itemEdit.item_pk_id || 0, q_no: itemEdit.q_no ?? null, ...body,
      });
      setItemEdit(null);
      await load();
    } catch (e) { say(e.message, true); } finally { setBusy(false); }
  };


  /* It ASKS, because this one cannot be undone — unlike every other control on these lists, which
     only move a row. The server refuses a row that came from the masterplan whatever the screen
     drew, since a rule living in the UI is not a rule. */
  const removeRow = async (list, id) => {
    /* NO `window.confirm` — the row's own bin asks in place now (GtmBin, 2026-10-08), and this
       was the last browser dialog left on a screen Max ruled popups off. Two asks for one delete
       is the page asking "did you mean it" twice, which is how people learn to click through
       both without reading either. */
    setBusy(true);
    try {
      await send(`${API}/${encodeURIComponent(code)}/${list}/row/${id}`, 'DELETE');
      await load();
    } catch (e) { say(e.message, true); } finally { setBusy(false); }
  };

  /* THE STOP CONDITIONS BELONG UNDER Q7 — Max, 2026-10-07: "nó nằm ở Q7 thì phải để dưới Q7 nhé
     chứ đưa lên đầu lại bị hỏi á". Slide 15 prints them at the foot of "How we measure success",
     and lifting them to the top of the page detached them from the only question that explains
     what they are, which is exactly what he had to ask about. */
  /* AND "(PROPOSED)" IS THE DOCUMENT'S OWN WORD, which was being dropped. Slide 15 heads this
     block 정지 조건 (제안) / "Stop conditions (proposed)" — Jihwan PUT THESE FORWARD, he did not
     settle them. Printing them as live rules, each with a chip beside it, was the portal
     promoting a proposal to a regulation, and that is half of why they read as unexplained. */
  const stopBlock = () => (
    <div className="gtm-stops">
      <h2>{t('gtm.stopHead')}</h2>
      {d.stops.length === 0 && <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>{t('gtm.stopNone')}</p>}
      {d.stops.map((s) => (
        <div className="gtm-stop" key={s.rule_pk_id}>
          {/* NO CHIP AND NO THRESHOLD — So-yeon Shin, 2026-10-16: "Could you clarify what 'Watched
              by hand' means?" and "Please remove the Threshold field". The chip named which of
              three evaluators read the rule; with the region columns off the sheet nothing is
              counted any more (gtm.js), so every condition is a sentence and the person running
              the programme writes where it stands. "Watched by hand" meant exactly that, and a
              label for the only kind left says nothing. */}
          <div className="gtm-stoptext">
            {may ? (
              <div className="gtm-stopedit">
                <input value={cellOf('rule', s, 'rule_pk_id', 'statement')}
                  aria-label={t('gtm.stopHead')} placeholder={t('gtm.stopHead')}
                  onChange={(e) => cellSet('rule', s.rule_pk_id, 'statement', e.target.value)} />
                <input className="gtm-stopnote-in"
                  value={cellOf('rule', s, 'rule_pk_id', 'note')}
                  placeholder={t('gtm.stopWhere')} aria-label={t('gtm.stopWhere')}
                  onChange={(e) => cellSet('rule', s.rule_pk_id, 'note', e.target.value)} />
                <GtmBin t={t} busy={busy} onRemove={() => dropChild('rule', s.rule_pk_id)} />
              </div>
            ) : (
              <>
                <p>{s.statement}</p>
                {/* Where it stands, in somebody's own words; nothing when nobody has written it. */}
                {!!(s.note || '').trim() && <small><b>{t('gtm.stopWhere')}:</b> {s.note}</small>}
              </>
            )}
          </div>
        </div>
      ))}
      {may && (
        <div className="gtm-stopadd">
          <input className="s-desc" value={stopNew.statement} placeholder={t('gtm.addStop')}
            aria-label={t('gtm.addStop')}
            onChange={(e) => setStopNew({ ...stopNew, statement: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addStop(); } }} />
          <input className="s-note" value={stopNew.note} placeholder={t('gtm.stopWhere')}
            aria-label={t('gtm.stopWhere')}
            onChange={(e) => setStopNew({ ...stopNew, note: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addStop(); } }} />
          <button type="button" className="gtm-btn" disabled={busy || !stopNew.statement.trim()}
            onClick={addStop}>
            <Plus size={13} style={{ verticalAlign: '-2px' }} /> {t('gtm.addLine')}
          </button>
        </div>
      )}
      <p className="gtm-stopnote">{t('gtm.stopNote')}</p>
    </div>
  );

  /* Q1 and Q2 carry the document's own tables; the rest do not. Named once so the two places
     that ask cannot drift into disagreeing about which questions have one. */
  /* Q5 and Q6 joined on 2026-10-07. The rule this feeds is "where there is a table, the prose is
     a note UNDER it" — the slides print their footnote after their table, and a paragraph sitting
     on top of one reads as an unfilled form. */
  const hasTable = (qNo) => (qNo === 1 && (d.personas || []).length > 0)
    || (qNo === 2 && (d.channels || []).length > 0)
    || (qNo === 5 && ((d.specs || []).length > 0 || may))
    || (qNo === 6 && ((d.discover || []).length > 0 || may))
    || (qNo === 7 && ((d.measures || []).length > 0 || may));

  const sectionBlock = (sections, asNote) => sections
    /* AN EMPTY NOTE IS NOT DRAWN WHILE THE SHEET IS LOCKED. Q6's whole body became the table on
       2026-10-07, so its section is now empty — and the block was still printing its "NOTE ON
       THIS TABLE" heading over nothing, which is the same unexplained empty box Max asked about
       on Q1. Unlocked it stays, because that is where somebody writes the note. */
    .filter((sec) => may || !asNote || sec.body)
    .map((sec) => (
    /* THE PORTAL'S OWN EDITOR, NOT A SECOND ONE — Max, 2026-10-07, pointing at the Note box on a
       project group: "cho nó có thể formatting giống vầy với nhé". RichText is already the answer
       to that on two screens, it carries the list indent fix the mail needed, and richToHtml reads
       the seven sections' existing PLAIN text unchanged — so nothing seeded from the masterplan
       has to be converted before it can be edited. */
    <div className={`gtm-qbody${asNote ? ' note' : ''}`} key={sec.section_pk_id}>
      {/* Under a table the box is a NOTE and says so, because an unlabelled editor under a list
          invites the question "what do I put here". Above, it is the answer and needs no label. */}
      {asNote && <div className="gtm-qnote-lab">{t('gtm.noteOnTable')}</div>}
      {may ? (
        <RichText
          value={drafts[sec.section_pk_id] ?? (sec.body || '')}
          onChange={(html) => setDrafts((prev) => ({ ...prev, [sec.section_pk_id]: html }))}
          placeholder={asNote ? t('gtm.noteOnTableHint') : t('gtm.emptySection')}
          /* A note is a line or two; reserving the answer's height for it is what made an empty
             box out of one sentence. */
          minHeight={asNote ? 44 : 92}
        />
      ) : sec.body ? (
        /* Read-only readers see the same markup the editor produces. */
        <div dangerouslySetInnerHTML={{ __html: richToHtml(sec.body) }} />
      ) : asNote ? null : t('gtm.emptySection')}
    </div>
  ));

  const itemBlock = (items, qNo) => {
    /* THE LAUNCH-STAGE FILTER CAME OFF — Max, 2026-10-08: "bỏ dùm tui nha tạm thời ko cần".
       The row above the questions is gone, so every item its question holds is drawn. The STAGE
       itself is untouched: it is still a column, still a field on the row and still a chip beside
       the item, so nothing recorded is lost and the row comes back by restoring one block. */
    const rows = items;
    const addingHere = itemEdit && !itemEdit.item_pk_id && itemEdit.q_no === qNo;
    return (
      <div className="gtm-items">
        {/* THE LIST SAYS WHAT IT IS — Max, 2026-10-07: "thiếu nè", on the blank band above the
            button. Q1, Q4 and Q7 carry no items, so the block was a 59px strip holding a lone
            control with nothing naming it. */}
        <div className="gtm-items-lab">{t('gtm.openItems')}</div>
        {rows.length === 0 && !addingHere && <p className="gtm-items-none">{t('gtm.noItems')}</p>}
        {rows.map((i) => (
          /* OPEN SHEET, OPEN ROW. There is no pencil any more: the sheet's one Edit put every
             row into its fields at once, and the sheet's one Save writes them. */
          may ? (
            <GtmItemRow key={i.item_pk_id} item={i} t={t} busy={busy}
              draft={itemDraft(i)} onDraft={(f, v) => cellSet('item', i.item_pk_id, f, v)}
              /* Removing is IMMEDIATE, not a draft — the row it would draft against is the thing
                 being removed, so there is nothing for the sheet's Save to write it onto. The row
                 asks first, which is where the safety lives. */
              onRemove={(x) => dropChild('item', x.item_pk_id)} />
          ) : (
            <OpenItem
              key={i.item_pk_id} t={t} item={i} canEdit={false}
              onSave={(b2) => child('item', b2)}
              onRemove={(id) => dropChild('item', id)}
            />
          )
        ))}
        {addingHere && (
          <GtmItemRow item={{ kind: 'todo' }} t={t} busy={busy}
            onSave={saveItem} onCancel={() => setItemEdit(null)} />
        )}
        {may && !addingHere && (
          <button type="button" className="gtm-btn small"
            onClick={() => setItemEdit({ q_no: qNo, kind: 'todo' })}>
            <Plus size={11} style={{ verticalAlign: '-1px' }} /> {t('gtm.addLine')}
          </button>
        )}
      </div>
    );
  };


  return (
    <div className="gtm-page">
      {/* ONE datalist for every owner box on the page. Rendered once: an <input list=…> points at
          an id, so sixteen copies of the same options would be sixteen copies in the DOM. */}
      {/* ONE SAVE FOR THE PAGE, AND IT IS ONLY THERE WHEN THERE IS SOMETHING TO SAVE. Stuck to the
          bottom of the viewport: seven open textareas make a long page, and a Save that lives at
          the end of it is a Save you scroll to find. It says how many sections changed, because
          "Save" on a page this size does not say what it is about to write. */}
      {canEdit && changed.length > 0 && (
        <div className="gtm-savebar">
          <span>{changed.length === 1
            ? t('gtm.oneChange')
            : t('gtm.nChanges', { count: changed.length })}</span>
          <button type="button" className="gtm-btn small primary" onClick={saveAll} disabled={busy}>
            {t('gtm.save')}
          </button>
          <button type="button" className="gtm-btn small" onClick={() => setDrafts({})} disabled={busy}>
            <X size={11} style={{ verticalAlign: '-1px' }} /> {t('gtm.discard')}
          </button>
        </div>
      )}
      <datalist id="gtm-people">
        {people.map((x) => <option key={x.username} value={x.name} />)}
      </datalist>
      {/* THE TOP STRIP IS FROZEN — Max, 2026-10-08: "freeze khúc trên lại nhé". This sheet runs
          to eight questions and four tables, and Save lived at the very top of it: changing one
          word three screens down meant scrolling back up to commit it, which is how people come
          to leave a sheet unsaved.
          A BAR OF ITS OWN, not the whole header. Sticking `.gtm-head` would have pinned the title,
          the headline, the owner and the launch date with it — about 200px of furniture while the
          sheet is open, over a page somebody is reading. The back button joins the actions because
          they were already rendering on one visual line, each at the top of its own column.
          `top: 0` against `.portal-shell`, which is the scrollport on this page. */}
      <div className="gtm-bar">
        <button type="button" className="pa-btn" onClick={onBack}>
          <ArrowLeft size={14} /> {approval ? t('gtm.backApproval') : t('gtm.back')}
        </button>
        <span className="gtm-bar-gap" />
        <div className="gtm-bar-acts">
          {/* ONE EDIT FOR THE WHOLE SHEET. It unlocks the header, the prose, every table and
              every list at once, and the Save beside it writes all of them. */}
          {canEdit && !editing && (
            <button type="button" className="pa-btn primary" onClick={() => {
              setHeadEdit({
                title: p.title || '', headline: p.headline || '',
                owner_name: p.owner_name || '',
                launch_target: (p.launch_target || '').toString().slice(0, 10),
              });
              setEditing(true);
            }}>
              <Pencil size={14} /> {t('gtm.edit')}
            </button>
          )}
          {canEdit && editing && (
            <>
              <button type="button" className="pa-btn primary" disabled={busy}
                onClick={async () => {
                  const headerChanged = !!headEdit && (
                    headEdit.title !== (p.title || '')
                    || headEdit.headline !== (p.headline || '')
                    || headEdit.owner_name !== (p.owner_name || '')
                    || headEdit.launch_target !== (p.launch_target || '').toString().slice(0, 10));
                  if (headerChanged) await saveHead();
                  await saveSheet(headerChanged);
                }}>
                <Check size={14} /> {t('gtm.save')}
              </button>
              <button type="button" className="pa-btn" disabled={busy}
                onClick={() => { setHeadEdit(null); cancelSheet(); }}>{t('gtm.cancel')}</button>
            </>
          )}
          {/* NO REFRESH — Max, 2026-10-08: "ko cần nút refresh nhé". Every write on this sheet
              reloads the programme itself, so the button answered a question the page had already
              answered, and sat beside Edit looking like one of the two things you do here. */}
          {/* EXPORT IS A FILE, NOT A DIALOG — Max, same day: "export ra PDF chứ ko phải là print
              nhé". It called `window.print()` for an hour, which is the browser's own engine and
              gives the best text, but it hands the person a dialog and a Save-as step rather than
              a document. The server renders the SAME PAGE in headless chromium and streams the
              file back; the `@media print` block is still the layout, so there is one rendering
              of this sheet and not two. */}
          <button type="button" className="pa-btn" disabled={pdfBusy} onClick={async () => {
            setPdfBusy(true);
            try { await downloadPdf(code); } catch (e) { say(e.message, true); } finally { setPdfBusy(false); }
          }}>
            <FileDown size={14} /> {pdfBusy ? t('gtm.exporting') : t('gtm.exportPdf')}
          </button>
          {canEdit && (
            /* Dead once sent, as on the list — the server refuses it too. */
            <button type="button" className="pa-btn pa-btn-drop" disabled={busy || DELETE_LOCKED.has(p.status)}
              title={DELETE_LOCKED.has(p.status)
                ? t(p.status === 'approved' ? 'gtm.deleteLockedApproved' : 'gtm.deleteLockedSent')
                : t('gtm.deleteProgramme')} onClick={async () => {
                /* ASK WITH THE NUMBER IN IT. "Delete this" and "delete this and its 87 rows" are
                   different questions, and only the server can count — so the count is fetched
                   before the dialog is drawn rather than guessed from what the screen shows. */
                try {
                  setBusy(true);
                  setDelAsk(await get(`${API}/${code}/delete-preflight`));
                } catch (e) { say(e.message, true); } finally { setBusy(false); }
              }}>
              <Trash2 size={14} /> {t('gtm.delete')}
            </button>
          )}
        </div>
      </div>

      <div className="gtm-head">
        <div style={{ minWidth: 0 }}>
          <div><span className="gtm-code">{p.gtm_code}</span></div>
          {/* THE HEADER EDITS WHERE IT READS. Its title, headline, owner, launch date and
              source document were the only things on the page that could not be changed at all
              (Max: "rùi nếu muốn sửa title thì làm sao") — and nothing here opens over the top of
              the page ("ko cần pop up nha cho điền trực tiếp luôn"). */}
          {/* THE HEADER FOLLOWS THE SHEET'S LOCK, not a flag of its own. It read `headEdit` alone,
              so after Save the page locked everywhere else and the header sat there still showing
              its fields — the one block that did not believe it had been saved. */}
          {may && headEdit ? (
            <div className="gtm-headedit">
              <label className="wide"><span>{t('gtm.fieldTitle')}</span>
                {/* The one field that keeps it: it is the first box on the sheet, so the cursor
                    lands where the Edit button was pressed rather than two screens away. */}
                <input autoFocus value={headEdit.title}
                  onChange={(e) => setHeadEdit({ ...headEdit, title: e.target.value })} /></label>
              <label className="wide"><span>{t('gtm.headline')}</span>
                <input value={headEdit.headline}
                  onChange={(e) => setHeadEdit({ ...headEdit, headline: e.target.value })} /></label>
              <label><span>{t('gtm.owner')}</span>
                <input list="gtm-people" value={headEdit.owner_name}
                  onChange={(e) => setHeadEdit({ ...headEdit, owner_name: e.target.value })} /></label>
              <label><span>{t('gtm.launchTarget')}</span>
                <input type="date" value={headEdit.launch_target}
                  onChange={(e) => setHeadEdit({ ...headEdit, launch_target: e.target.value })} /></label>
              {/* NO SOURCE DOCUMENT FIELD — Max, 2026-10-07: "ko cần trường Source document".
                  It is where this programme was read FROM, set once by the import, and not a thing
                  anybody fills in. AND IT NO LONGER PRINTS HERE EITHER — Max, 2026-10-08:
                  "bỏ luôn dòng này". It is the one fact on that line nobody working the
                  programme acts on, sitting beside the owner and the launch date, which they do.
                  It stays on the PDF COVER, where a reader who did not open this screen has no
                  other way to learn where the programme came from, and `source_doc` is untouched
                  in the database. */}

            </div>
          ) : (
            <>
              <h1 style={{ marginTop: 6 }}>{p.title}</h1>
              {p.headline && <p className="gtm-sub">{p.headline}</p>}
              <div className="gtm-rowfacts" style={{ marginTop: 8 }}>
                <span>{t('gtm.owner')}: <b>{p.owner_name || t('gtm.notSet')}</b></span>
                <span>{t('gtm.launchTarget')}: <b>{day(p.launch_target) || t('gtm.notSet')}</b></span>
              </div>
            </>
          )}
        </div>

      </div>

      {/* WHERE THE PROGRAMME STANDS, AND THE ONE CONTROL THAT MOVES IT — Max, 2026-10-08:
          "Add a review and approval function. Current approver: Young."

          ONE BAND, NOT A CARD ROW. The four cards above count work that is still open; this says
          whether the sheet as a whole has been agreed. Those are different questions and a fifth
          card would have made this one more number to scan past.

          IT ALWAYS SAYS WHO IT IS WAITING ON, in every state, because "approved" with no name
          beside it cannot be told from "approved by whoever happened to press it". When the
          account that decided is NOT the named approver — an Admin acting because the approver
          cannot reach the screen — both are printed. */}
      <div className={`gtm-review ${p.status || 'draft'}`}>
        <div className="gtm-review-head">
          <span className="gtm-review-state">{t(`gtm.rev_${p.status || 'draft'}`)}</span>
          <span className="gtm-review-who">
            {t('gtm.approver')}: <b>{p.approver_name || t('gtm.notSet')}</b>
          </span>
          {p.submitted_at && (
            <span className="gtm-review-when">
              {t('gtm.sentForReview')}: <b>{day(p.submitted_at)}</b>
              {p.submitted_by ? ` · ${p.submitted_by}` : ''}
            </span>
          )}
          {p.decided_at && DECIDED.has(p.status) && (
            <span className="gtm-review-when">
              {t(p.status === 'approved' ? 'gtm.approvedOn' : 'gtm.rejectedOn')}: <b>{day(p.decided_at)}</b>
              {/* The ACCOUNT that pressed it, printed only when it is not the person named above —
                  otherwise the line would say the same name twice. */}
              {p.decided_by && p.decided_by !== p.approver_name ? ` · ${p.decided_by}` : ''}
            </span>
          )}
          <span className="gtm-review-sp" />
          {canEdit && p.status !== 'in_review' && (
            /* PROJECT APPROVAL'S WORDS AND BUTTONS: navy Submit for a draft, Resubmit for one that
               came back. A RE-REVIEW SAYS SO — submitting an approved sheet again is legitimate,
               the items under it go on being answered for months, but calling it "Send for
               review" would hide that there is already a decision on the row. */
            <button type="button"
              className={`pa-btn${(p.status || 'draft') === 'draft' ? ' submit' : ''}`} disabled={busy}
              onClick={() => review('submit')}>
              {(p.status || 'draft') === 'draft' ? <Mail size={14} /> : <RotateCcw size={14} />}
              {p.status === 'approved' ? t('gtm.reviewAgain')
                : p.status === 'rejected' ? t('gtm.resubmit') : t('gtm.sendForReview')}
            </button>
          )}
          {approval && d.canDecide && p.status === 'in_review' && (
            /* ONE BUTTON, and the dialog carries the two answers — the same shape Project
               Approval's row has. Two buttons here would ask somebody to decide before the
               dialog has shown them what they are deciding on. ONLY ON THE APPROVER'S SCREEN. */
            <button type="button" className="pa-btn approve" disabled={busy}
              onClick={() => { setRevNote(''); setDecisionFor({ action: 'approve' }); }}>
              <CheckCircle2 size={14} /> {t('gtm.approve')}
            </button>
          )}
          {/* NO "Decide in GTM Approval" link here — Max, 2026-10-08: "trạng thái này bị dư". The
              author's sheet does not point at the decision at all; GTM Approval is its own menu. */}
        </div>
        {/* THE NOTE SURVIVES THE DECISION. A rejection whose reason vanished the moment the
            author opened the sheet would be a refusal with no explanation, which is what the
            required box exists to prevent. */}
        {p.decision_note && DECIDED.has(p.status) && (
          <p className="gtm-review-note">{p.decision_note}</p>
        )}
      </div>

      {/* THE PORTAL'S OWN DECISION DIALOG, not a fourth one. `spec.group` is all it needs of the
          thing being decided, and a GTM programme fills it the way a project group does: the code
          is the id people read the row by, the title is its name. */}
      {decisionFor && (
        <DecisionDialog
          spec={{ ...decisionFor, group: { groupId: code, groupName: p.title } }}
          note={revNote}
          onNote={setRevNote}
          busy={busy}
          selectedIds={[]}
          onSwitch={(action) => setDecisionFor((x) => ({ ...x, action }))}
          onCancel={() => { setDecisionFor(null); setRevNote(''); }}
          onConfirm={() => review(decisionFor.action, revNote)}
        />
      )}

      {delAsk && (
        <GtmDeleteDialog t={t} code={code} title={d?.programme?.title}
          rows={delAsk.rows} photos={delAsk.photos} busy={busy}
          onCancel={() => setDelAsk(null)}
          onConfirm={async () => {
            setBusy(true);
            try {
              await send(`${API}/${code}`, 'DELETE');
              setDelAsk(null);
              onBack();
            } catch (e) { say(e.message, true); setBusy(false); }
          }} />
      )}

      {/* THE COVER, ON PAPER ONLY — Max, 2026-10-08: "cái file export pdf bạn sẽ format lại cho
          đẹp á. Bạn có thể dựa vào cái master plan để tham khảo format". So the reference is
          Jihwan's own deck, read out of its stylesheet rather than guessed at: an eyebrow at
          10.5px letter-spaced 0.3em, a heavy display title, hairline black rules, one cyan.
          The PDF will be read beside that deck, so it should not look like a different document.
          `display: none` on screen and `block` in print — the page people USE is unchanged. */}
      <div className="gtm-cover" aria-hidden="true">
        <p className="gtm-cover-eyebrow">{t('gtm.coverEyebrow')}</p>
        <p className="gtm-cover-code">{p.gtm_code}</p>
        <h1 className="gtm-cover-title">{p.title}</h1>
        {p.headline && <p className="gtm-cover-head">{p.headline}</p>}
        {/* THE REVIEW STATE IS ON THE COVER, NOT ABOVE IT. The band printed as the first thing on
            page one, over the title it describes — Max, on the PDF, 2026-10-08. On paper it is
            two more facts in this grid, and the band itself is hidden in print. */}
        <div className="gtm-cover-facts">
          <div><span>{t('gtm.coverStatus')}</span><b>{t(`gtm.rev_${p.status || 'draft'}`)}
            {p.submitted_at && p.status === 'in_review' ? ` · ${t('gtm.sentForReview')} ${day(p.submitted_at)}` : ''}
            {p.decided_at && DECIDED.has(p.status) ? ` · ${day(p.decided_at)}` : ''}</b></div>
          <div><span>{t('gtm.approver')}</span><b>{p.approver_name || t('gtm.notSet')}</b></div>
          {p.decision_note && DECIDED.has(p.status) && (
            <div className="gtm-cover-wide"><span>{t('gtm.coverDecisionNote')}</span><b>{p.decision_note}</b></div>
          )}
          <div><span>{t('gtm.owner')}</span><b>{p.owner_name || t('gtm.notSet')}</b></div>
          <div><span>{t('gtm.launchTarget')}</span><b>{p.launch_target
            ? String(p.launch_target).slice(0, 10) : t('gtm.notSet')}</b></div>
          <div><span>{t('gtm.sourceDoc')}</span><b>{p.source_doc || '—'}</b></div>
          <div><span>{t('gtm.printedOn')}</span><b>{new Date().toISOString().slice(0, 10)}</b></div>
        </div>
        <div className="gtm-cover-counts">
          <div><b>{d.totals.open_decisions}</b><span>{t('gtm.openDecisions')}</span></div>
          <div><b>{d.totals.open_verifies}</b><span>{t('gtm.openVerifies')}</span></div>
        </div>
      </div>

      {msg && <div className={`gtm-msg ${msg.bad ? 'bad' : 'ok'}`}>{msg.text}</div>}

      {/* THE SUMMARY, ABOVE THE PROSE. Four counts and the stop conditions: what is blocked, before
          what it says. The commit count carries its own threshold beside it, because "0" means
          nothing without the 500 it is being judged against. */}
      <div className="gtm-counts">
        <div className={`gtm-count${d.totals.open_decisions ? ' warn' : ''}`}>
          <div className="k">{t('gtm.openDecisions')}</div>
          <div className="n">{d.totals.open_decisions}</div>
        </div>
        <div className={`gtm-count${d.totals.open_verifies ? ' warn' : ''}`}>
          <div className="k">{t('gtm.openVerifies')}</div>
          <div className="n">{d.totals.open_verifies}</div>
        </div>
        {/* "Regions awaiting reply" and "Units committed" came off with the region columns they
            counted (So-yeon, 2026-10-16: "keep only the Region and Note fields"). */}
      </div>


      {/* THE LAUNCH-STAGE FILTER ROW IS GONE — Max, 2026-10-08: "bỏ dùm tui nha tạm thời ko
          cần". It cut the seven questions by before / pre / launch / post, and on this programme
          it had one pill to offer: five items carry `before` and fourteen carry no stage at all,
          so pressing it hid fourteen of the nineteen. That was honest — the line under it said so
          — but a filter whose only setting hides three quarters of the sheet is not yet earning
          the row it sits in.
          TEMPORARY, AND NOTHING WAS THROWN AWAY. `phase` is still a column, still a field on the
          item row and still a chip beside the item, so the stages people have recorded are intact
          and the row comes back by restoring this block and the `work` fold that fed it (git,
          this commit). The strings, the CSS and `gtm.phaseWhy_*` are all still in place. */}
      {/* THE PROJECT SKUs THIS PROGRAMME LAUNCHED, above the seven questions: once a programme is
          approved this is what it turned into, and the approver's view reads it too. */}
      <GtmSkuLinks t={t} code={code} skus={d.skus || []} may={may}
        onChanged={load} say={say} />
      {/* ONE CONTROL FOR ALL SEVEN, because folding a long sheet one question at a time is seven
          clicks to do the thing somebody wanted once. */}
      {(d.questions || []).length > 0 && (
        <div className="gtm-qfold">
          <button type="button" className="pa-btn"
            onClick={() => setShut((prev) => (prev.size
              ? new Set()
              : new Set([...(d.questions || []).map((x) => x.q), 0])))}>
            {shut.size ? t('gtm.expandAll') : t('gtm.collapseAll')}
          </button>
        </div>
      )}
      {/* THE SEVEN. They come from the server, in the document's own words. */}
      {(d.questions || []).map((q) => {
        const bucket = byQuestion.get(q.q) || { sections: [], items: [] };
        return (
          <div className={`gtm-q${shut.has(q.q) ? ' shut' : ''}`} key={q.q}>
            {/* THE WHOLE HEAD IS THE CONTROL, not a chevron somebody has to aim at — and a
                BUTTON, so it is reachable from a keyboard and says whether it is open. */}
            <button type="button" className="gtm-qhead" aria-expanded={!shut.has(q.q)}
              onClick={() => toggleQ(q.q)}>
              <ChevronDown size={15} className="gtm-qchev" aria-hidden="true" />
              <span className="gtm-qno">{q.q}</span>
              <h2>{q.en}</h2>
              {/* A SHUT QUESTION STILL SAYS WHAT IS IN IT. Folding a section that shows nothing
                  about its contents is hiding, not summarising — so the head carries the count of
                  what is still open under it the moment it closes. */}
              {shut.has(q.q) && (
                <span className="gtm-qsum">
                  {t('gtm.qOpenCount', {
                    n: (bucket.items || []).filter((i) => i.status === 'open').length,
                  })}
                </span>
              )}
              {/* THE EDIT BUTTON IS GONE — Max, 2026-10-07: "mỗi section phải click nút edit thì
                  nó bị phiền á". Seven questions meant seven hunts for a small button at the end
                  of a heading before a word could be changed. The prose opens when you click it,
                  which is where the hand already is. Save and Cancel stay, because this is a
                  paragraph somebody is composing and a blur that silently commits half a thought
                  is worse than one extra click. */}
            </button>
            {!shut.has(q.q) && (
            <>
            {/* THE PROSE GOES UNDER THE TABLE WHERE THERE IS ONE — Max, 2026-10-07, circling
                this box on Q1: "tôi chưa hiểu mục đích của cái này". Fair, and the cause was
                placement: once the ten personas became rows, all that was left here was the
                slide's FOOTNOTE about the table — and slides 6 and 7 both print their note AFTER
                their table. Sitting on top of it, unlabelled, in a tall editor, it read as an
                unfilled form rather than as a remark on the thing below.
                Q3-Q7 have no table, so there the prose IS the answer and leads as it always did. */}
            {!hasTable(q.q) && sectionBlock(bucket.sections)}
            {/* THE DOCUMENT'S OWN TABLE, WHERE THE DOCUMENT PUTS IT. Slide 6 is Q1 and slide 7
                is Q2, so each list is drawn under its own question rather than in a block of its
                own — the same reason the stop conditions moved down to Q7. */}
            {/* DRAWN WHILE THE SHEET IS OPEN EVEN WHEN EMPTY — So-yeon, 2026-10-16, could not find
                the priority list the guide shows: on a new programme there were no rows, so the
                list, its Add button and the priority all stayed hidden. New programmes also start
                with the standard ten now (gtm.js STANDARD_PERSONAS), to TICK rather than type. */}
            {q.q === 1 && ((d.personas || []).length > 0 || may) && (
              <GtmRankList
                rows={d.personas || []} idKey="persona_pk_id" canEdit={may} bulk={may} t={t} busy={busy}
                selectable selectLabel={t('gtm.select')} selectHint={t('gtm.selectHint')}
                onSave={(order) => saveOrder('personas', order)}
                onEdits={(ch) => { listEdits.current['personas'] = ch; }}
                onAdd={(body) => addRow('personas', body).then(loadLists)}
                catalog={lists.persona} catalogNewHint={t('gtm.listNew')} catalogEmpty={t('gtm.listAllIn')}
                catalogDup={t('gtm.listDup')}
                onRemove={(r) => removeRow('personas', r.persona_pk_id)}
                aboveLabel={t('gtm.targetAudience')} belowLabel={t('gtm.notATarget')}
                headName={t('gtm.persona')} headNote={t('gtm.remarks')}
                addLabel={t('gtm.addPersona')}
                /* THE LETTER IS GONE FROM THE SCREEN — Max: "tôi nghĩ ko cần A, B C hay D á".
                   It is still STORED, because A-J is how slide 6 names these ten and it is what
                   traces a row back there; nothing on the page reads by it, since the rank is the
                   position and the name is the name. */
                renderRow={(r) => (
                  <>
                    <b className="gtm-rankname">{r.name}</b>
                    <small className="gtm-ranknote">{r.note}</small>
                  </>
                )}
              />
            )}
            {q.q === 2 && ((d.channels || []).length > 0 || may) && (
              <GtmRankList
                rows={d.channels || []} idKey="channel_pk_id" canEdit={may} bulk={may} t={t} busy={busy}
                selectable selectLabel={t('gtm.select')} selectHint={t('gtm.selectHint')}
                onSave={(order) => saveOrder('channels', order)}
                onEdits={(ch) => { listEdits.current['channels'] = ch; }}
                onAdd={(body) => addRow('channels', body).then(loadLists)}
                catalog={lists.channel} catalogNewHint={t('gtm.listNew')} catalogEmpty={t('gtm.listAllIn')}
                catalogDup={t('gtm.listDup')}
                onRemove={(r) => removeRow('channels', r.channel_pk_id)}
                aboveLabel={t('gtm.channelsInUse')} belowLabel={t('gtm.channelsOut')}
                headName={t('gtm.channel')} headNote={t('gtm.remarks')}
                addLabel={t('gtm.addChannel')}
                /* THE DOT IS THE COMPANY'S STATUS, NOT THIS PROGRAMME'S, which is what the
                   slide's own header says. Read-only on purpose: two programmes editing it would
                   disagree about one company fact. Passed as `leading` so it survives the unlock
                   — it is the one fact on the row an editor cannot change.
                   `channel_no` IS STORED AND NOT DRAWN, for the same reason the persona letter is
                   not: printing it beside the rank would put two numbers answering two different
                   questions on one row. */
                leading={(r) => (
                  <span className={`gtm-dot ${r.status}`} title={t(`gtm.chStatus_${r.status}`)} />
                )}
                renderRow={(r) => (
                  <>
                    <b className="gtm-rankname">
                      <span className={`gtm-dot ${r.status}`}
                        title={t(`gtm.chStatus_${r.status}`)} />
                      {r.name}
                    </b>
                    <small className="gtm-ranknote">{r.note}</small>
                  </>
                )}
              />
            )}
            {/* SLIDES 8 AND 9 ARE Q2 AS WELL — Max, 2026-10-07: "phần này cũng nằm trong channel
                nè mà hiện tôi ko thấy", then "này nữa". The layer above the sales channels was a
                paragraph in the note below, and the regions were drawn at the FOOT of the page,
                nowhere near the question they answer. Both read in the document's own order:
                channels, then what moves above them, then the order of acts, then the region each
                reader comes from. */}
            {/* A layer row is not ranked, so it is created through the same generic child writer
                the regions use — no id on the body means INSERT. */}
            {q.q === 2 && (d.layers || []).length > 0 && (
              <LayerTable t={t} d={d} canEdit={may} busy={busy}
                cellOf={cellOf} cellSet={cellSet}
                onRemove={(id) => dropChild('layer', id)}
                /* The same one-transaction writer every other list uses; a second "save this
                   order" is two places for a move to mean something different. */
                /* The same one-transaction writer every other list uses; a second "save this
                   order" is two places for a move to mean something different. */
                onOrder={(order) => saveOrder('layer', order)}
                onAdd={(b) => child('layer', b)} />
            )}
            {q.q === 2 && (d.steps || []).length > 0 && (
              <div className="gtm-sub-tbl">
                <div className="gtm-items-lab">{t('gtm.orderHead')}</div>
                {/* THE SAME RANKED LIST, with ONE half: every step is in the order, so the number
                    is purely the position and there is no "not in this programme" to fall into. */}
                <GtmRankList
                  rows={d.steps} idKey="step_pk_id" canEdit={may} bulk={may} t={t} busy={busy}
                  singleHalf nameKey="title" noteKey="detail"
                  onSave={(order) => saveOrder('steps', order)}
                  onEdits={(ch) => { listEdits.current['steps'] = ch; }}
                  onAdd={(body) => addRow('steps', body)}
                  onRemove={(r) => removeRow('steps', r.step_pk_id)}
                  aboveLabel={t('gtm.orderLabel')} belowLabel=""
                  headName={t('gtm.step')} headNote={t('gtm.stepDetail')}
                  addLabel={t('gtm.addStep')}
                  renderRow={(r) => (
                    <>
                      <b className="gtm-rankname">{r.title}</b>
                      <small className="gtm-ranknote">{r.detail}</small>
                    </>
                  )}
                />
              </div>
            )}
            {q.q === 2 && (
              <RegionTable t={t} d={d} canEdit={may} busy={busy}
                cellOf={cellOf} cellSet={cellSet} catalog={lists.region}
                onAdd={(b) => child('region', b).then(loadLists)}
                onRemove={(id) => dropChild('region', id)}
                onOrder={(order) => saveOrder('regions', order)} />
            )}
            {/* Q5 IS SLIDE 12 — the comparison, then the photographs it points at. Max,
                2026-10-07: "question 5 thì cho nó dạng bảng và cho phép upload ảnh và ghi chú
                của ảnh nhé". The document's own two images are captioned to point at a part of
                the chair, which is why the caption is part of the feature. */}
            {q.q === 5 && (
              <GtmSpecTable t={t} d={d} canEdit={may} busy={busy}
                cellOf={cellOf} cellSet={cellSet}
                onAdd={(b) => addLine('spec', b)}
                onRemove={(id) => dropLine('spec', id)}
                onOrder={(order) => saveOrder('specs', order)} />
            )}
            {q.q === 5 && (
              <div className="gtm-sect">
                <h2>{t('gtm.photosHead')}</h2>
                <p className="hint">{t('gtm.photosHint')}</p>
                {/* THE PORTAL'S OWN PHOTO BAND, not a second one — one upload, one compression,
                    one viewer, one delete. `readOnly` follows the sheet lock, so a photograph is
                    added and captioned in the same state everything else on this page is edited
                    in. `onChange` reloads the programme, because the caption rows come down with
                    it and a new photo has none yet. */}
                <ProjectPhotos
                  scope="gtm" ownerKey={code} hideTitle readOnly={!may}
                  captions captionOf={capOf}
                  onCaption={(ph, v) => cellSet('photo', ph.photoId, 'caption', v)}
                  onChange={load}
                />
              </div>
            )}
            {q.q === 6 && (
              <GtmDiscover t={t} d={d} canEdit={may} busy={busy}
                cellOf={cellOf} cellSet={cellSet}
                onAdd={(b) => addLine('discover', b)}
                onRemove={(id) => dropLine('discover', id)} />
            )}
            {hasTable(q.q) && sectionBlock(bucket.sections, true)}
            {itemBlock(bucket.items, q.q)}
            {/* Q7 IS "HOW DO WE MEASURE SUCCESS", so its metrics belong under it and so do the
                stop conditions that read them — the same move slides 8 and 9 just made. The
                metrics were at the foot of the page, two screens from the question they answer,
                which is how the stop conditions came to be asked about in the first place. */}
            {/* THE METRICS TABLE IS OFF THE SHEET — Max, 2026-10-08: "nhiệm vụ của chúng ta chỉ
                cần làm bảng để user điền vào 7 mục đó thui... nếu có yêu cầu khác thì chờ họ
                review nên phần này bạn xóa nha." He is right about the scope: the deliverable is
                a form for the seven questions, and a table that reads live Shopify sales and
                freezes a baseline is a second feature nobody has asked for.
                THE DATA AND THE ENDPOINT STAY. `00_PORTAL_gtm_metric` keeps its seven rows
                (backed up by sql/20261008e) and the payload still carries them, because the three
                frozen baselines cannot be rebuilt — a Shopify order refunded since would make
                re-taking one return a different figure, which is the reason it was frozen in the
                first place. Bringing the block back is this one line; the rows could not be. */}
            {/* Q7'S OWN TABLE, where the metrics table used to be. */}
            {q.q === 7 && (
              <GtmMeasure t={t} d={d} canEdit={may} busy={busy}
                cellOf={cellOf} cellSet={cellSet}
                onAdd={(b) => addLine('measure', b)}
                onRemove={(id) => dropChild('measure', id)}
                onOrder={(order) => saveOrder('measures', order)} />
            )}
            {q.q === 7 && stopBlock()}
            </>
            )}
          </div>
        );
      })}

      {/* SLIDE 16, at the foot and under no question — it is how the programme is RUN, not one of
          the seven things it is. Drawn whenever the sheet is unlocked too, so an empty one can be
          started rather than being invisible until somebody seeds it. */}
      {((d.timeline || []).length > 0 || may) && (
        <GtmTimeline t={t} d={d} canEdit={may} busy={busy}
          cellOf={cellOf} cellSet={cellSet} progOf={progOf} progSet={progSet}
          onAdd={(b) => addLine('timeline', b)}
          onRemove={(id) => dropChild('timeline', id)}
          onOrder={(order) => saveOrder('timeline', order)} />
      )}

      {loose.length > 0 && (
        /* THE CLASS HAD TO MOVE WITH THE STATE. `shut.has(0)` already hid this block's body and
           drew its count, but the CARD kept a plain className — so it folded while its chevron
           went on pointing down and it kept the padding of an open card. Two halves of one fact,
           and only one of them was patched. */
        <div className={`gtm-q${shut.has(0) ? ' shut' : ''}`}>
          {/* `gtm.title` is the PAGE's name ("GTM Programmes"), printed here over the items that
              belong to the programme rather than to any one question — the same borrowed-label
              fault the Title field had.
              IT FOLDS LIKE THE SEVEN. It is a block of the same kind, and a head that looks like
              theirs and does not answer to the same click is the page teaching two rules. `0` is
              its key, which no question carries. */}
          <button type="button" className="gtm-qhead" aria-expanded={!shut.has(0)}
            onClick={() => toggleQ(0)}>
            <ChevronDown size={15} className="gtm-qchev" aria-hidden="true" />
            <h2>{t('gtm.acrossProgramme')}</h2>
            {shut.has(0) && (
              <span className="gtm-qsum">
                {t('gtm.qOpenCount', { n: loose.filter((i) => i.status === 'open').length })}
              </span>
            )}
          </button>
          {!shut.has(0) && itemBlock(loose, null)}
        </div>
      )}

    </div>
  );
}

/* ---- regions ---------------------------------------------------------------------------------- */

/* WHAT MOVES ABOVE THE SALES CHANNELS — slide 8, and Q2's as much as the eleven channels are.
   Max, 2026-10-07: "phần này cũng nằm trong channel nè mà hiện tôi ko thấy". It was a paragraph in
   Q2's note; slide 7's own footnote says why it is not among the eleven ("They sit above the sales
   channels, so they get their own table on the next slide").

   The regions table beside it is the shape this follows — cell by cell, saved on blur — because
   that is what this screen already does for a three-column table and a second way of editing one
   is the thing Max spent an afternoon correcting. */
function LayerTable({ t, d, canEdit, busy, onRemove, onAdd, onOrder, cellOf, cellSet }) {
  const [adding, setAdding] = useState(false);
  const [row, setRow] = useState({ stage: '', role: '', condition: '' });
  /* BUILT LIKE THE ORDER TABLE BELOW IT — Max, 2026-10-08: "design lại bảng stage cho giống bảng
     ở dưới nhé và có thể cái tính năng kéo line để sắp xếp thứ tự". It was a `<table>` with three
     column headers while its neighbour is a ranked list with a grip, a number and a drag; two
     shapes for two tables that sit three inches apart and are read in one pass.

     THE NUMBER EARNS ITS PLACE ONLY BECAUSE THESE ARE ORDERED NOW. It would have been a claim an
     hour ago, when the rows had no order to carry — the same reason the ranked lists print one and
     the spec table does not.

     A LOCAL COPY, like every other draggable list here: the row has to follow the hand before the
     round trip, and the server's answer replaces it afterwards — except mid-drag and mid-save,
     which would pull it out from under the hand. */
  const [list, setList] = useState(d.layers || []);
  const [from, setFrom] = useState(null);
  const [over, setOver] = useState(null);
  const dirty = useRef(false);
  useEffect(() => {
    if (from === null && !dirty.current) setList(d.layers || []);
  }, [d.layers, from]);

  const commit = (next) => {
    setList(next);
    dirty.current = true;
    Promise.resolve(onOrder(next.map((r) => ({ id: r.layer_pk_id }))))
      .finally(() => { dirty.current = false; });
  };
  const move = (i, by) => {
    const j = i + by;
    if (j < 0 || j >= list.length) return;
    const next = list.slice();
    const [r] = next.splice(i, 1);
    next.splice(j, 0, r);
    commit(next);
  };
  const drop = (to) => {
    if (from === null || to === from) { setFrom(null); setOver(null); return; }
    const next = list.slice();
    const [r] = next.splice(from, 1);
    next.splice(from < to ? to - 1 : to, 0, r);
    setFrom(null); setOver(null);
    commit(next);
  };

  return (
    <div className="gtm-sub-tbl gtm-layer-tbl">
      <div className="gtm-items-lab">{t('gtm.layerHead')}</div>
      <div className="gtm-lgrid">
        {/* THE HEAD IS DRAWN LOCKED TOO — Max, on the PDF, 2026-10-08: the table heads should
            stand out. This was the one table on the sheet that named its columns only while it
            was being edited, so the reader of the sheet and of the PDF got three unlabelled
            columns. */}
        <div className="gtm-lrow gtm-lhead" aria-hidden="true">
          <span /><span />
          <span>{t('gtm.stage')}</span><span>{t('gtm.role')}</span><span>{t('gtm.condition')}</span>
          <span /><span />
        </div>
        {list.map((r, i) => (
          <div className={`gtm-lrow${over === i && from !== null ? ' over' : ''}`}
            key={r.layer_pk_id}
            draggable={canEdit}
            onDragStart={() => setFrom(i)}
            onDragOver={(e) => { if (from !== null) { e.preventDefault(); setOver(i); } }}
            onDragEnd={() => { setFrom(null); setOver(null); }}
            onDrop={(e) => { e.preventDefault(); drop(i); }}>
            <span className="gtm-lgrip">
              {canEdit && <GripVertical size={12} aria-hidden="true" />}
            </span>
            <span className="gtm-rankno">{i + 1}</span>
            {['stage', 'role', 'condition'].map((f) => (
              /* DRAFTED, NOT SAVED ON BLUR. Under one page-wide Save, a cell that wrote itself the
                 moment the cursor left would make Cancel a lie. */
              canEdit ? (
                <input key={f} className={`gtm-l-${f}`}
                  aria-label={t(`gtm.${f}`)} placeholder={t(`gtm.${f}`)}
                  value={cellOf('layer', r, 'layer_pk_id', f)}
                  onChange={(e) => cellSet('layer', r.layer_pk_id, f, e.target.value)} />
              ) : (
                <span key={f} className={`gtm-l-${f}${f === 'stage' ? ' strong' : ''}`}>
                  {r[f] || <i className="gtm-nil">—</i>}
                </span>
              )
            ))}
            {canEdit && (
              <>
                <span className="gtm-tmove">
                  <button type="button" disabled={busy || i === 0}
                    title={t('gtm.moveUp')} aria-label={t('gtm.moveUp')}
                    onClick={() => move(i, -1)}><ChevronUp size={13} /></button>
                  <button type="button" disabled={busy || i === list.length - 1}
                    title={t('gtm.moveDown')} aria-label={t('gtm.moveDown')}
                    onClick={() => move(i, 1)}><ChevronDown size={13} /></button>
                </span>
                <span className="gtm-rowdrop">
                  <GtmBin t={t} busy={busy} fromDoc={!!r.from_doc}
                    label={t('gtm.removeRow')} onRemove={() => onRemove(r.layer_pk_id)} />
                </span>
              </>
            )}
          </div>
        ))}
        {adding && (
          <div className="gtm-lrow adding">
            <span className="gtm-lgrip" /><span className="gtm-rankno" />
            {['stage', 'role', 'condition'].map((f, i) => (
              <input key={f} className={`gtm-l-${f}`} autoFocus={i === 0} value={row[f]}
                placeholder={t(`gtm.${f}`)} aria-label={t(`gtm.${f}`)}
                onChange={(e) => setRow({ ...row, [f]: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Escape') setAdding(false); }} />
            ))}
            <span className="gtm-ladd-acts">
              <button type="button" className="gtm-btn small primary" disabled={!row.stage.trim()}
                onClick={() => {
                  onAdd(row); setRow({ stage: '', role: '', condition: '' }); setAdding(false);
                }}>{t('gtm.add')}</button>
              <button type="button" className="gtm-btn small"
                onClick={() => setAdding(false)}>{t('gtm.cancel')}</button>
            </span>
            <span />
          </div>
        )}
      </div>
      {canEdit && !adding && (
        <button type="button" className="gtm-rankadd-btn" onClick={() => setAdding(true)}>
          <Plus size={12} /> {t('gtm.addStage')}
        </button>
      )}
    </div>
  );
}

function RegionTable({ t, d, canEdit, busy, onAdd, onRemove, onOrder, cellOf, cellSet, catalog = [] }) {
  const [newName, setNewName] = useState('');
  /* PICKED FROM THE SHARED LIST — Max, 2026-10-16: "Regions, channel cũng vậy là cho sẵn danh sách
     và có thể add thêm giá trị mới dùng cho lần sau". The regions this programme holds are left out;
     a name typed that is not on the list joins it (the server adds it). */
  const low = (v) => String(v || '').trim().toLowerCase();
  const have = new Set((d.regions || []).map((r) => low(r.region_name)));
  const avail = catalog.filter((n) => !have.has(low(n)));
  const q = low(newName);
  const isDup = !!q && have.has(q);
  const isNew = !!q && !isDup && !catalog.some((n) => low(n).includes(q));
  const addRegion = () => {
    if (!newName.trim() || isDup) return;
    onAdd({ region_name: newName.trim(), seq: (d.regions || []).length + 1 });
    setNewName('');
  };
  /* THE SAME LOCAL COPY EVERY DRAGGABLE LIST HERE KEEPS: the row follows the hand before the
     round trip, and the server's answer replaces it afterwards — never mid-drag or mid-save. */
  const [rows, setRows] = useState(d.regions || []);
  const [from, setFrom] = useState(null);
  const [over, setOver] = useState(null);
  const dirty = useRef(false);
  useEffect(() => {
    if (from === null && !dirty.current) setRows(d.regions || []);
  }, [d.regions, from]);
  const commit = (next) => {
    setRows(next);
    dirty.current = true;
    Promise.resolve(onOrder(next.map((r) => ({ id: r.region_pk_id }))))
      .finally(() => { dirty.current = false; });
  };
  const move = (i, by) => {
    const j = i + by;
    if (j < 0 || j >= rows.length) return;
    const next = rows.slice();
    const [r] = next.splice(i, 1);
    next.splice(j, 0, r);
    commit(next);
  };
  const drop = (to) => {
    if (from === null || to === from) { setFrom(null); setOver(null); return; }
    const next = rows.slice();
    const [r] = next.splice(from, 1);
    next.splice(from < to ? to - 1 : to, 0, r);
    setFrom(null); setOver(null);
    commit(next);
  };
  return (
    <div className="gtm-sect">
      <h2>{t('gtm.regionsHead')}</h2>
      {/* ITS OWN SENTENCE. This read `gtm.stopNote` — "The portal flags these. It does not act on
          them." — which is the STOP CONDITIONS' line, printed over a table about regions. */}
      <p className="hint">{t('gtm.regionsHint')}</p>
      <div className="gtm-tablewrap">
        <table className="gtm-table gtm-regions-tbl">
          {/* THE COLUMNS ARE SIZED TO WHAT THEY HOLD — Max, 2026-10-07: "phần này design xấu".
              A tick and a quantity were sharing the width of a sentence, the note was squeezed
              into what was left, and a two-line note made its whole row taller than the rest so
              the table read as floating boxes rather than rows. */}
          {/* REGION AND NOTE, NOTHING ELSE — So-yeon Shin, 2026-10-16: "Please keep only the Region
              and Note fields and remove the others." Person, reply status, consent, commit quantity
              and reply due came off the SCREEN; the columns stay in the table with what GTM-26-001
              holds in them, so bringing one back is a column here and not a migration. The order is
              the priority, by the same drag as every other list on the sheet. */}
          <colgroup>
            <col style={{ width: 54 }} />
            <col style={{ width: 260 }} />
            <col />
            {/* THE MOVERS' COLUMN EXISTS ONLY WHILE THE SHEET IS OPEN — locked, an empty column
                would stop the navy band and every row's rule short of the tables around it. */}
            {canEdit && <col style={{ width: 96 }} />}
          </colgroup>
          <thead>
            <tr>
              <th aria-label={t('gtm.orderHead')} />
              <th>{t('gtm.region')}</th>
              <th>{t('gtm.note')}</th>
              {canEdit && <th aria-label={t('gtm.colAction')} />}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.region_pk_id}
                className={over === i && from !== null ? 'over' : undefined}
                draggable={canEdit}
                onDragStart={() => setFrom(i)}
                onDragOver={(e) => { if (from !== null) { e.preventDefault(); setOver(i); } }}
                onDragEnd={() => { setFrom(null); setOver(null); }}
                onDrop={(e) => { e.preventDefault(); drop(i); }}>
                <td className="gtm-rgrip">
                  {/* The flex goes on a span: `display: flex` on a `<td>` takes the cell out of
                      the table layout model, which stopped the column aligning and clipped the
                      grip. */}
                  <span className="gtm-rgrip-in">
                    {canEdit && <GripVertical size={12} aria-hidden="true" />}
                    <span className="gtm-rankno">{i + 1}</span>
                  </span>
                </td>
                {/* The region's name can be corrected now that it is one of only two fields. */}
                <td className="gtm-region-name">{canEdit ? (
                  <input value={cellOf('region', r, 'region_pk_id', 'region_name')}
                    aria-label={t('gtm.region')}
                    onChange={(e) => cellSet('region', r.region_pk_id, 'region_name', e.target.value)} />
                ) : r.region_name}</td>
                {/* THE NOTE IS A CELL LIKE THE REST — Max, 2026-10-07: "này phải sửa dc luôn
                    chứ". A textarea, because these run to two lines. */}
                <td className="gtm-region-note">{canEdit ? (
                  <textarea rows={2} className="gtm-regnote" aria-label={t('gtm.note')}
                    value={cellOf('region', r, 'region_pk_id', 'note')}
                    onChange={(e) => cellSet('region', r.region_pk_id, 'note', e.target.value)} />
                ) : <span style={{ fontSize: 13.5, color: '#475569' }}>{r.note}</span>}</td>
                {canEdit && (
                  <td className="gtm-ract">
                    <span className="gtm-tmove">
                      <button type="button" disabled={busy || i === 0}
                        title={t('gtm.moveUp')} aria-label={t('gtm.moveUp')}
                        onClick={() => move(i, -1)}><ChevronUp size={13} /></button>
                      <button type="button" disabled={busy || i === rows.length - 1}
                        title={t('gtm.moveDown')} aria-label={t('gtm.moveDown')}
                        onClick={() => move(i, 1)}><ChevronDown size={13} /></button>
                    </span>
                    <GtmBin t={t} busy={busy} fromDoc={!!r.from_doc}
                      label={t('gtm.removeRow')} onRemove={() => onRemove(r.region_pk_id)} />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {canEdit && (
        <div className="gtm-regionadd">
          <div style={{ display: 'flex', gap: 7 }}>
            {/* A DROPDOWN (Max: "làm dropdown chứ ko phải tab"); a region has nothing else to type
                when added, so a pick adds it at once. */}
            <GtmPickInput value={newName} onChange={setNewName} options={avail}
              placeholder={t('gtm.listPick')} ariaLabel={t('gtm.region')} emptyText={t('gtm.listAllIn')}
              onPick={(n) => { onAdd({ region_name: n, seq: (d.regions || []).length + 1 }); setNewName(''); }}
              onEnter={addRegion}
              style={{ font: 'inherit', fontSize: 14, padding: '5px 9px', border: '1px solid #cbd5e1', borderRadius: 6, width: 300, height: 32 }} />
            <button type="button" className="gtm-btn small" disabled={busy || !newName.trim() || isDup} onClick={addRegion}>
              <Plus size={11} style={{ verticalAlign: '-1px' }} /> {t('gtm.addRegion')}
            </button>
          </div>
          {(isDup || isNew) && (
            <div className={`gtm-rankpick-note${isDup ? ' bad' : ''}`}>{isDup ? t('gtm.listHave') : t('gtm.listNew')}</div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---- metrics ----------------------------------------------------------------------------------- */
export default function Gtm({ mode }) {
  const { t } = useTranslation();
  const { code } = useParams();
  const navigate = useNavigate();
  const approval = mode === 'approval';
  return (
    <div className="sp-page">
      <Header />
      <div className="sp-main">
        {/* The module's tabs on the two lists only; a programme's own sheet has its back button. */}
        {!code && <ModuleTabs module="gtm" />}
        {/* THE ROUTE SAYS WHICH ROLE — /gtm is the author's, /gtm-approval the approver's — and the
            sheet is told, so its back button returns to the list it came from and it offers only
            that role's buttons. */}
        {code
          ? (
            <Programme code={code} t={t} mode={approval ? 'approval' : 'author'}
              onBack={() => navigate(approval ? '/gtm-approval' : '/gtm')} />
          )
          : approval
            ? <ApprovalList t={t} onOpen={(c) => navigate(`/gtm-approval/${c}`)} />
            /* `edit` rides in the URL rather than in router state, so the link can be pasted and
               a refresh lands in the same place — the same reason `?print=1` is a parameter. */
            : <ProgrammeList t={t} onOpen={(c, edit) => navigate(`/gtm/${c}${edit ? '?edit=1' : ''}`)} />}
      </div>
    </div>
  );
}
